import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { condividiReport as condividiReportCondiviso } from '../lib/condivisione';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ProspectTicker from '../components/ProspectTicker';
import ProspectBoot from '../components/ProspectBoot';
import ProspectFilters from '../components/ProspectFilters';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import {
  fetchMappa, fetchFilterOptions,
  startAstemaster, astemasterStatus, creaLinkCondivisione,
  togglePreferito as apiTogglePreferito,
  toggleScarta as apiToggleScarta,
  fetchProspectRadar, saveProspectRadar,
  updateSavedSearch,
  fetchTutorialImmobile,
  api,
} from '../lib/api';
import FilterPanel, { countActiveFilters } from '../components/FilterPanel';
import ProfiloInvestitoreForm from '../components/ProfiloInvestitoreForm';
import { tipoNome, luoghi, parseIndirizzo } from '../lib/tipologia';
import ProspectCard from '../components/ProspectCard';
import { PROSPECT_VC, PROSPECT_VC_KEY, VLABEL } from '../components/prospectShared';
import { openMapAroundMe } from '../lib/geo';
import OmiReportModal from '../components/OmiReportModal';

// Lucide-style icon factory (consistent with the rest of the app)
const Icon = ({ size = 14, stroke = 1.75, children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ISearch = (p) => (<Icon {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></Icon>);
const IList = (p) => (<Icon {...p}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></Icon>);
const IExpand = (p) => (<Icon {...p}><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></Icon>);
const ICollapse = (p) => (<Icon {...p}><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></Icon>);
const IFilter = (p) => (<Icon {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></Icon>);

const VERDETTO_FILTERS = [
  { id: '',             label: 'Tutti i verdetti' },
  { id: 'eccezionale',  label: '⭐ Eccezionale' },
  { id: 'consigliata',  label: '✅ Consigliata' },
  { id: 'con_riserva',  label: '⚠️ Con riserva' },
  { id: 'da_evitare',   label: '🚫 Da evitare' },
  { id: 'in_analisi',   label: '🔵 In analisi' },
  { id: 'analizzate',   label: 'Già analizzate' },
  { id: 'da_analizzare',label: 'Da analizzare' },
];

// Colori verdetto — replica esatta vecchia mappa Filament
const VERDETTO_COLORS = {
  eccezionale: '#16a34a',  // verde scuro
  consigliata: '#65a30d',  // lime/oliva
  con_riserva: '#d97706',  // amber/arancione
  da_evitare:  '#dc2626',  // rosso
  in_analisi:  '#2563eb',  // blu = job queued/running
  null:        '#737373',  // grigio = non analizzato
};

// Stile Google Maps per il prospect: grayscale + solo strade (no POI/transit/business)
const PROSPECT_MAP_STYLE = [
  // Tutto desaturato (effetto scala di grigi)
  { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 4 }] },
  { elementType: 'labels.text.fill', stylers: [{ saturation: -100 }, { color: '#5A5E64' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  // Nascondi tutti i POI (hotel, ristoranti, ospedali, scuole, parchi attractions, business, ecc.)
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  // Mantieni i parchi MA in grigio chiaro
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#E8E7E2' }] },
  // Trasporti pubblici nascosti (stazioni metro, autobus, ecc.)
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  // Strade: chiare ma leggibili, con nomi
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#E2E1DB' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#FAF9F6' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#D8D6D0' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6E7278' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  // Terreno background
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#EFEEE9' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#EBEAE5' }] },
  // Acqua in grigio leggero
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#D8D6D0' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#888B91' }] },
  // Amministrative (regioni/province): bordi chiari, niente etichette se non essenziali
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#C9C7C0' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
];

// Verdict config: per ogni esito, fattori "AI" mostrati durante la fase 'accumulate'
// (ricostruzione fake-but-believable, copia delle factor list del design BTC).
// Punteggi positivi/negativi che vengono sommati per arrivare al `target` score.
// PROSPECT_VC, PROSPECT_VC_KEY, VLABEL importati da components/prospectShared
// (condivisi con <ProspectCard>). 2026-07-13.

// Palette markers ridotta per il prospect redesign
const PROSPECT_VERDETTO_COLORS = {
  eccezionale: '#16915E',
  consigliata: '#16915E',
  con_riserva: '#C68A1A',
  da_evitare:  '#B23A2F',
  in_analisi:  '#2563EB',   // blu = analisi in corso (job attivo)
  nuovo:       '#0EA5E9',   // azzurro = nuovo (uscito dopo l'ultima visita)
  null:        '#5A5E64',
};

const fmtEuro0 = (n) => n != null
  ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
  : '—';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return '—'; }
};

// Combina verdetto + stato job in una "categoria" usata per colore e cluster.
// Priorità verdetto > in_analisi > non_analizzato (un immobile già analizzato
// con verdetto vince anche se per qualche motivo c'è un job attivo).
function categoryFor(r) {
  if (r.rivelasta_verdetto) return r.rivelasta_verdetto;
  if (r.job_status === 'running' || r.job_status === 'queued') return 'in_analisi';
  return 'non_analizzato';
}
function markerColor(category, preferito) {
  if (preferito) return '#f97316';
  if (category === 'non_analizzato') return VERDETTO_COLORS.null;
  return VERDETTO_COLORS[category] || VERDETTO_COLORS.null;
}

// Ordine settori (replica vecchio Filament + in_analisi prima dei non analizzati)
const CLUSTER_BUCKETS = [
  { key: 'eccezionale',    color: VERDETTO_COLORS.eccezionale },
  { key: 'consigliata',    color: VERDETTO_COLORS.consigliata },
  { key: 'con_riserva',    color: VERDETTO_COLORS.con_riserva },
  { key: 'da_evitare',     color: VERDETTO_COLORS.da_evitare },
  { key: 'in_analisi',     color: VERDETTO_COLORS.in_analisi },
  { key: 'non_analizzato', color: VERDETTO_COLORS.null },
];

// Renderer cluster — replica esatta vecchio Filament:
// pie pieno conic-gradient, 44px fisso, cerchio nero 28px col numero bianco
const CLUSTER_SIZE = 44;
const CLUSTER_INNER = 28;
// ── AdvancedMarkerElement (marker "moderni") — helper di migrazione ──────────
// Marina 2026-07-11: passaggio da google.maps.Marker (legacy) ad AdvancedMarkerElement.
// Le icone restano le stesse SVG data-URI: le incapsuliamo in un <img> come `content`.
// AdvancedMarkerElement ancora il bottom-center del content alla posizione; qui
// riproduciamo l'anchor originale dell'icona con una translate.
function iconToContent(icon) {
  const w = (icon.scaledSize && icon.scaledSize.width) || 36;
  const h = (icon.scaledSize && icon.scaledSize.height) || 36;
  const ax = (icon.anchor && icon.anchor.x != null) ? icon.anchor.x : w / 2;
  const ay = (icon.anchor && icon.anchor.y != null) ? icon.anchor.y : h;
  const img = document.createElement('img');
  img.src = icon.url;
  img.width = w; img.height = h;
  img.style.display = 'block';
  const wrap = document.createElement('div');
  wrap.style.transform = `translate(${w / 2 - ax}px, ${h - ay}px)`;
  wrap.appendChild(img);
  return wrap;
}
function advMarker(google, { position, icon, title, zIndex }) {
  const m = new google.maps.marker.AdvancedMarkerElement({
    position, content: iconToContent(icon), title, gmpClickable: true,
  });
  if (zIndex != null) m.zIndex = zIndex;
  return m;
}
// {lat,lng} da un AdvancedMarkerElement (position può essere LatLng o literal).
function mLL(m) {
  const p = m && m.position;
  if (!p) return null;
  return {
    lat: typeof p.lat === 'function' ? p.lat() : p.lat,
    lng: typeof p.lng === 'function' ? p.lng() : p.lng,
  };
}

// Ray-casting point-in-polygon. path = [{lat,lng}, ...]; pt = {lat,lng}.
function pointInPolygon(pt, path) {
  if (!path || path.length < 3) return true;
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const xi = path[i].lng, yi = path[i].lat;
    const xj = path[j].lng, yj = path[j].lat;
    const intersect = ((yi > pt.lat) !== (yj > pt.lat)) &&
      (pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
// Bounding box (w,s,e,n) di un path.
function pathBounds(path) {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  path.forEach((p) => { w = Math.min(w, p.lng); e = Math.max(e, p.lng); s = Math.min(s, p.lat); n = Math.max(n, p.lat); });
  return { w, s, e, n };
}

// Cluster renderer "prospect": pallino grigio scuro pieno con numero bianco, semplice e leggibile
// Builder dell'icona cluster prospect — factored così possiamo
// ricolorarla on-the-fly al variare della card selezionata nel carousel.
function buildProspectClusterIcon(google, color, count) {
  const size = 48;
  const cx = size / 2;
  const r = 17;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${cx}" cy="${cx}" r="${r}" fill="${color}" stroke="#fff" stroke-width="2"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.30))"/>
      <text x="${cx}" y="${cx}"
            text-anchor="middle" dominant-baseline="central"
            font-family="Manrope, system-ui, sans-serif"
            font-size="13" font-weight="700"
            fill="#ffffff">${count}</text>
    </svg>
  `;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(cx, cx),
  };
}
// Conteggio per categoria dei marker di un cluster (con override opzionale per
// l'immobile appena rivelato, il cui _category potrebbe non essere ancora aggiornato).
function prospectClusterCounts(markers, override) {
  const counts = { eccezionale: 0, consigliata: 0, con_riserva: 0, da_evitare: 0, in_analisi: 0, nuovo: 0, non_analizzato: 0 };
  (markers || []).forEach((m) => {
    let v = m && m._category;
    if (override && m && m._immobileId === override.id) v = override.cat;
    if (v in counts) counts[v] += 1; else counts.non_analizzato += 1;
  });
  return counts;
}
const PROSPECT_CLUSTER_BUCKETS = [
  ['eccezionale', PROSPECT_VERDETTO_COLORS.eccezionale],
  ['consigliata', PROSPECT_VERDETTO_COLORS.consigliata],
  ['con_riserva', PROSPECT_VERDETTO_COLORS.con_riserva],
  ['da_evitare',  PROSPECT_VERDETTO_COLORS.da_evitare],
  ['in_analisi',  PROSPECT_VERDETTO_COLORS.in_analisi],
  ['nuovo',       PROSPECT_VERDETTO_COLORS.nuovo],
  ['non_analizzato', PROSPECT_VERDETTO_COLORS.null],
];
// True se nel cluster c'è almeno un immobile con un risultato (analizzato o in analisi).
function prospectClusterHasResult(counts) {
  return (counts.eccezionale + counts.consigliata + counts.con_riserva + counts.da_evitare + counts.in_analisi + counts.nuovo) > 0;
}
// Cluster a "torta" coi colori dei verdetti (stile B2B), palette prospect.
function buildProspectClusterPie(google, counts, count) {
  const size = 48, cx = size / 2, cy = size / 2, r = size / 2 - 2;
  let segments = '';
  let start = -Math.PI / 2;
  PROSPECT_CLUSTER_BUCKETS.forEach(([key, color]) => {
    const n = counts[key];
    if (!n) return;
    const frac = n / count;
    const end = start + frac * Math.PI * 2;
    if (frac >= 0.999) {
      segments += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/>`;
    } else {
      const large = end - start > Math.PI ? 1 : 0;
      const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
      const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
      segments += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z" fill="${color}"/>`;
    }
    start = end;
  });
  const inner = 30;
  // Cluster che contiene NUOVI → anello azzurro + badge col numero di nuovi.
  const nNew = counts.nuovo || 0;
  const ring = nNew > 0 ? `<circle cx="${cx}" cy="${cy}" r="${size / 2 - 1.6}" fill="none" stroke="#0EA5E9" stroke-width="3"/>` : '';
  const badge = nNew > 0 ? `
      <circle cx="${size - 8}" cy="8" r="8" fill="#0EA5E9" stroke="#fff" stroke-width="1.5"/>
      <text x="${size - 8}" y="8" text-anchor="middle" dominant-baseline="central"
            font-family="Manrope, system-ui, sans-serif" font-size="9.5" font-weight="800" fill="#fff">${nNew > 9 ? '9+' : nNew}</text>` : '';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${cx}" cy="${cy}" r="${size / 2 - 0.5}" fill="#fff" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.30))"/>
      ${segments}
      ${ring}
      <circle cx="${cx}" cy="${cy}" r="${inner / 2}" fill="#1F2328"/>
      <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
            font-family="Manrope, system-ui, sans-serif" font-size="13" font-weight="700" fill="#fff">${count}</text>
      ${badge}
    </svg>
  `;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(cx, cx),
  };
}
const prospectClusterRenderer = {
  render({ count, position, markers }) {
    const google = window.google;
    const counts = prospectClusterCounts(markers);
    // Cluster "vergine" (nessun risultato) → pallino grigio pulito come prima;
    // se contiene almeno un immobile analizzato/in analisi → torta coi colori.
    const icon = prospectClusterHasResult(counts)
      ? buildProspectClusterPie(google, counts, count)
      : buildProspectClusterIcon(google, '#5A5E64', count);
    return advMarker(google, {
      position,
      icon,
      title: `${count} immobili`,
      zIndex: 1000000 + count,
    });
  },
};

const clusterRenderer = {
  render({ count, position, markers }) {
    const google = window.google;
    const counts = { eccezionale: 0, consigliata: 0, con_riserva: 0, da_evitare: 0, in_analisi: 0, non_analizzato: 0 };
    markers.forEach((m) => {
      const v = m._category;
      if (v in counts) counts[v] += 1;
      else counts.non_analizzato += 1;
    });

    const size = CLUSTER_SIZE;
    const cx = size / 2, cy = size / 2, r = size / 2 - 1.5; // -1.5 per il border 3px

    // Pie pieno (path SVG per ogni settore, ordine fisso)
    let segments = '';
    let startAngle = -Math.PI / 2;
    CLUSTER_BUCKETS.forEach((b) => {
      const n = counts[b.key];
      if (n === 0) return;
      const frac = n / count;
      const endAngle = startAngle + frac * Math.PI * 2;
      if (frac >= 0.999) {
        segments += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${b.color}"/>`;
      } else {
        const large = endAngle - startAngle > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        segments += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z" fill="${b.color}"/>`;
      }
      startAngle = endAngle;
    });

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${cx}" cy="${cy}" r="${size/2 - 0.5}" fill="white"/>
        ${segments}
        <circle cx="${cx}" cy="${cy}" r="${CLUSTER_INNER/2}" fill="#0a0a0a"/>
        <text x="${cx}" y="${cy}"
              text-anchor="middle" dominant-baseline="central"
              font-family="Geist, sans-serif"
              font-size="12" font-weight="700"
              fill="#ffffff">${count}</text>
      </svg>
    `;

    return advMarker(google, {
      position,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
      title: `${count} immobili`,
      zIndex: 1000000 + count,
    });
  },
};

// Marker style "prospect redesign": pallino pieno piccolo, no fumetto, no ring preferito.
// ViewBox 36x36 con padding intorno al cerchio (r=10) per dare spazio alla drop-shadow
// senza che venga clippata dai bordi dell'SVG → niente "alone quadrato" attorno al pin.
function buildProspectMarkerIcon(google, color) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="10" fill="${color}" stroke="#fff" stroke-width="2"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.32))"/>
    </svg>
  `;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(36, 36),
    anchor: new google.maps.Point(18, 18),
  };
}

// Marker prospect a "fumetto prezzo" (stile immobiliare.it): pill col prezzo,
// colore = categoria (verdetto/nuovo/grigio), coda in basso sul punto.
function buildProspectPriceMarker(google, color, priceLabel) {
  const label = String(priceLabel);
  const fs = 12.5, padX = 9, charW = 7.3;
  const w = Math.max(40, Math.ceil(padX * 2 + label.length * charW));
  const h = 24, tail = 6, totalH = h + tail;
  const rx = h / 2, cx = w / 2;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${totalH}" viewBox="0 0 ${w} ${totalH}">
      <g filter="drop-shadow(0 2px 3px rgba(0,0,0,0.30))">
        <path d="M ${cx - tail} ${h - 1} L ${cx} ${h + tail} L ${cx + tail} ${h - 1} Z" fill="${color}"/>
        <rect x="1" y="1" width="${w - 2}" height="${h - 1}" rx="${rx}" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      </g>
      <text x="${cx}" y="${(h) / 2 + 1}" text-anchor="middle" dominant-baseline="central"
            font-family="Manrope, system-ui, sans-serif" font-size="${fs}" font-weight="800" fill="#fff">${label}</text>
    </svg>
  `;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(w, totalH),
    anchor: new google.maps.Point(cx, totalH),
  };
}

// Contenuto DOM per un marker "in analisi": pallino colorato + anello che gira
// (Web Animations API → gira davvero, a differenza di un SVG dentro <img>).
function buildProspectAnalyzingContent(color) {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:relative;width:40px;height:40px;transform:translate(-50%,-50%);pointer-events:auto;';
  const ring = document.createElement('div');
  ring.style.cssText = `position:absolute;inset:0;border-radius:50%;box-sizing:border-box;border:3px solid ${color}2E;border-top-color:${color};filter:drop-shadow(0 1px 3px rgba(0,0,0,.28));`;
  try { ring.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], { duration: 850, iterations: Infinity }); } catch (_) {}
  const dot = document.createElement('div');
  dot.style.cssText = `position:absolute;top:50%;left:50%;width:18px;height:18px;border-radius:50%;background:${color};transform:translate(-50%,-50%);box-shadow:0 2px 5px rgba(0,0,0,.3);border:2px solid #fff;`;
  try { dot.animate([{ opacity: 0.65 }, { opacity: 1 }, { opacity: 0.65 }], { duration: 1200, iterations: Infinity }); } catch (_) {}
  wrap.appendChild(ring); wrap.appendChild(dot);
  return wrap;
}
// OverlayView per i marker prospect: ogni marker è un <div> reale,
// animato via CSS transition/keyframes (GPU compositor). Sostituisce
// google.maps.Marker per il path prospect, dove servono animazioni fluide
// (dim opacity, pulse halo) senza il costo di setOpacity/setIcon nativi.
let __ProspectMarkerOverlayClass = null;
function getProspectMarkerOverlayClass(google) {
  if (__ProspectMarkerOverlayClass) return __ProspectMarkerOverlayClass;
  __ProspectMarkerOverlayClass = class extends google.maps.OverlayView {
    constructor({ position, row, color, isPrimary, isDrop, onClick }) {
      super();
      this.position = position;
      this.row = row;
      this.color = color;
      this.isPrimary = !!isPrimary;
      this.isDrop = !!isDrop;
      this.onClick = onClick;
      this.el = null;
      this._immobileId = row.id;
      this._prospectColor = color;
    }
    onAdd() {
      const el = document.createElement('div');
      el.className = 'pr-marker';
      if (this.isPrimary) el.classList.add('pr-marker--bounce');
      else if (this.isDrop) el.classList.add('pr-marker--drop');
      el.style.setProperty('--pr-marker-color', this.color);
      el.innerHTML = '<div class="pr-marker__halo"></div><div class="pr-marker__dot"></div>';
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (typeof this.onClick === 'function') this.onClick();
      });
      this.el = el;
      this.getPanes().overlayMouseTarget.appendChild(el);
    }
    draw() {
      if (!this.el) return;
      const proj = this.getProjection();
      if (!proj) return;
      const px = proj.fromLatLngToDivPixel(new google.maps.LatLng(this.position.lat, this.position.lng));
      if (!px) return;
      this.el.style.transform = `translate3d(${Math.round(px.x)}px, ${Math.round(px.y)}px, 0)`;
    }
    onRemove() {
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
      this.el = null;
    }
    setSelected(v) { if (this.el) this.el.classList.toggle('is-selected', !!v); }
    setDim(v)      { if (this.el) this.el.classList.toggle('is-dim',      !!v); }
    getPosition()  { return new google.maps.LatLng(this.position.lat, this.position.lng); }
    setOpacity()   {}
    setIcon()      {}
    setZIndex(v)   { if (this.el) this.el.style.zIndex = v == null ? '' : String(v); }
  };
  return __ProspectMarkerOverlayClass;
}

// Halo prospect via OverlayView nel pane "overlayLayer" (sotto markerLayer)
// → quando il pulse fa scale piccolo, il bordo dell'onda passa SOTTO il pin
// invece che sopra, eliminando la "sovrapposizione" visibile ad ogni ciclo.
let __ProspectHaloOverlayClass = null;
function getProspectHaloOverlayClass(google) {
  if (__ProspectHaloOverlayClass) return __ProspectHaloOverlayClass;
  __ProspectHaloOverlayClass = class extends google.maps.OverlayView {
    constructor() { super(); this.latLng = null; this.color = '#999'; this.el = null; this._active = false; }
    onAdd() {
      const el = document.createElement('div');
      el.className = 'pr-halo';
      el.style.setProperty('--halo-color', this.color);
      if (this._active) el.classList.add('is-active');
      this.el = el;
      const panes = this.getPanes();
      (panes.overlayLayer || panes.markerLayer).appendChild(el);
    }
    draw() {
      if (!this.el || !this.latLng) return;
      const proj = this.getProjection();
      if (!proj) return;
      const p = proj.fromLatLngToDivPixel(this.latLng);
      if (!p) return;
      this.el.style.transform = `translate3d(${Math.round(p.x)}px, ${Math.round(p.y)}px, 0)`;
    }
    onRemove() {
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
      this.el = null;
    }
    setLatLng(latLng) { this.latLng = latLng; this.draw(); }
    setColor(color)   { this.color = color || '#999'; if (this.el) this.el.style.setProperty('--halo-color', this.color); }
    setActive(active) { this._active = !!active; if (this.el) this.el.classList.toggle('is-active', !!active); }
  };
  return __ProspectHaloOverlayClass;
}

// Variante "selected": pallino + halo che pulsa (replica KF.pulse del design BTC,
// scale 0.8 → 2, opacity .5 → 0, 1800ms ease-out infinite).
// [LEGACY] Mantenuta per fallback, ma non più usata dopo migrazione a OverlayView.
function buildProspectMarkerSelectedIcon(google, color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
    <g transform="translate(40 40)">
      <circle cx="0" cy="0" r="12" fill="none" stroke="${color}" stroke-width="2" opacity="0.55">
        <animate attributeName="r" values="12;28" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.55;0" dur="1.6s" repeatCount="indefinite"/>
      </circle>
      <circle cx="0" cy="0" r="10" fill="${color}" stroke="#fff" stroke-width="2"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.32))"/>
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(80, 80),
    anchor: new google.maps.Point(40, 40),
  };
}
// Variante con SMIL <animateTransform>: il marker entra con scale .4 → 1.45 → 1
// (replica esatta di KF.marker del design BTC, 520ms cubic-bezier).
// SMIL nei data: URL funziona su Chromium/Safari moderni; fallback statico se non supportato.
function buildProspectMarkerBounceIcon(google, color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <g transform="translate(14 14)">
      <circle cx="0" cy="0" r="10" fill="${color}" stroke="#fff" stroke-width="2"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.32))"/>
      <animateTransform attributeName="transform" type="scale"
                        values="0.4;1.45;1" keyTimes="0;0.6;1"
                        dur="0.52s" begin="0s" fill="freeze"
                        additive="sum" />
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(28, 28),
    anchor: new google.maps.Point(14, 14),
  };
}

// SVG marker singolo — pallino + (opzionale) fumetto prezzo sopra
function buildMarkerIcon(google, color, isPreferito, priceLabel = null) {
  if (!priceLabel) {
    // Versione semplice (no prezzo): solo dot 26x26 ancorato al centro
    const ring = isPreferito
      ? '<circle cx="13" cy="13" r="12" fill="none" stroke="#f97316" stroke-width="2"/>'
      : '';
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">
        ${ring}
        <circle cx="13" cy="13" r="9" fill="${color}" stroke="#fff" stroke-width="2"
                filter="drop-shadow(0 1px 2px rgba(0,0,0,0.4))"/>
      </svg>
    `;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
      scaledSize: new google.maps.Size(26, 26),
      anchor: new google.maps.Point(13, 13),
    };
  }
  // Versione con fumetto prezzo sopra il pallino
  // SVG 80x46: pill 56x18 top centrato + freccia in basso + pallino sotto
  // Anchor al centro del pallino (40, 36) → la lat/lng cade sul dot
  const pillWidth = Math.max(48, priceLabel.length * 8.5 + 12);
  const cx = 40, dotY = 36;
  const pillX = cx - pillWidth / 2;
  const ring = isPreferito
    ? `<circle cx="${cx}" cy="${dotY}" r="11" fill="none" stroke="#f97316" stroke-width="2"/>`
    : '';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="46" viewBox="0 0 80 46">
      <rect x="${pillX}" y="2" width="${pillWidth}" height="18" rx="9"
            fill="#0a0a0a" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.3))"/>
      <text x="${cx}" y="14" text-anchor="middle" fill="white"
            font-family="Geist, system-ui, sans-serif" font-size="11" font-weight="600">${priceLabel}</text>
      <polygon points="${cx-4},20 ${cx+4},20 ${cx},25" fill="#0a0a0a"/>
      ${ring}
      <circle cx="${cx}" cy="${dotY}" r="8" fill="${color}" stroke="#fff" stroke-width="2"
              filter="drop-shadow(0 1px 2px rgba(0,0,0,0.4))"/>
    </svg>
  `;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg.trim()),
    scaledSize: new google.maps.Size(80, 46),
    anchor: new google.maps.Point(cx, dotY),
  };
}

// Formato prezzo compatto per il fumetto: 143000 → "€143k", 1250000 → "€1.2M"
function formatPriceLabel(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return null;
  if (n >= 1_000_000) return '€' + (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1).replace('.', ',') + 'M';
  if (n >= 1_000)     return '€' + Math.round(n / 1_000) + 'k';
  return '€' + n;
}

// Singleton init (Maps API caricato una sola volta per sessione)
let googleLoadPromise = null;
// Street View thumbnail per la card prospect. Approccio diretto: l'immagine
// viene sempre richiesta; se manca copertura Google ritorna un grigio "no
// imagery" (200 OK, no error). Fallback solo su errori di rete reali.
function StreetViewPhoto({ row, onOpen }) {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const lat = row?.lat, lng = row?.lng;
  const [err, setErr] = useState(false);
  if (!key || lat == null || lng == null || err) {
    return <span className="pr-card__photo-lbl">foto<br/>immobile</span>;
  }
  const src = `https://maps.googleapis.com/maps/api/streetview?size=186x186&location=${lat},${lng}&fov=80&key=${key}`;
  return (
    <button type="button" className="pr-card__photo-link"
            aria-label="Apri Street View"
            onClick={(e) => { e.stopPropagation(); onOpen && onOpen({ lat, lng, address: row?.indirizzo }); }}>
      <img src={src} alt="" className="pr-card__photo-img"
           loading="lazy" onError={() => setErr(true)} />
    </button>
  );
}

// CardChars importato da components/prospectShared (usato dentro <ProspectCard>).

// Overlay del mini-tutorial: oscura tutto tranne il rettangolo target (spotlight)
// e mostra un fumetto. Se rect è null → schermo scuro pieno + card centrale.
// Marina/Michael 2026-07-14.
function TutorialLayer({ rect, info, onSkip }) {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 780;
  // Opacità ridotta (la mappa/card sotto si deve intravedere) e maschera SOLO
  // visiva (pointer-events none) così il target sotto resta cliccabile. 2026-07-14.
  const dark = 'rgba(15,23,42,0.5)';
  // I rettangoli scuri CATTURANO i click (bloccano l'area oscurata); il "buco"
  // dello spotlight non è coperto da nessun div → solo lì si può interagire.
  const mask = { position: 'fixed', background: dark, zIndex: 3000, pointerEvents: 'auto' };
  let tip;
  if (rect) {
    const below = (rect.y + rect.h / 2) < vh * 0.55;
    tip = below ? { top: rect.y + rect.h + 16, left: 14, right: 14 } : { bottom: vh - rect.y + 16, left: 14, right: 14 };
  } else {
    tip = { top: '50%', left: 14, right: 14, transform: 'translateY(-50%)' };
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, fontFamily: 'Manrope, system-ui, sans-serif', pointerEvents: 'none' }}>
      {rect ? (
        <>
          <div style={{ ...mask, left: 0, top: 0, width: '100%', height: Math.max(0, rect.y) }} />
          <div style={{ ...mask, left: 0, top: rect.y + rect.h, width: '100%', bottom: 0 }} />
          <div style={{ ...mask, left: 0, top: rect.y, width: Math.max(0, rect.x), height: rect.h }} />
          <div style={{ ...mask, left: rect.x + rect.w, top: rect.y, right: 0, height: rect.h }} />
          <div style={{ position: 'fixed', left: rect.x - 5, top: rect.y - 5, width: rect.w + 10, height: rect.h + 10, borderRadius: rect.circle ? '50%' : 16, border: '2.5px solid #4FD89E', boxShadow: '0 0 0 3px rgba(79,216,158,0.3), 0 0 22px rgba(79,216,158,0.5)', zIndex: 3001, pointerEvents: 'none', animation: 'tutPulse 1.4s ease-in-out infinite' }} />
        </>
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: dark, pointerEvents: 'auto' }} />
      )}
      <style>{'@keyframes tutPulse{0%,100%{box-shadow:0 0 0 3px rgba(79,216,158,0.3),0 0 22px rgba(79,216,158,0.5)}50%{box-shadow:0 0 0 6px rgba(79,216,158,0.18),0 0 30px rgba(79,216,158,0.7)}}@keyframes tutIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>

      <div style={{ position: 'fixed', ...tip, zIndex: 3002, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ maxWidth: 360, width: '100%', background: '#fff', color: '#1F2937', borderRadius: 16, padding: '15px 16px', boxShadow: '0 16px 44px rgba(0,0,0,0.4)', pointerEvents: 'auto', animation: 'tutIn .28s ease-out both' }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.42 }}>{info.text}</div>
          {info.cta ? (
            <button onClick={info.onCta} style={{ marginTop: 13, width: '100%', border: 0, background: '#16915E', color: '#fff', borderRadius: 12, padding: '12px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer' }}>{info.cta}</button>
          ) : info.hideTap ? null : (
            <div style={{ marginTop: 9, fontSize: 12, color: '#16915E', fontWeight: 800 }}>👆 Tocca l'area evidenziata</div>
          )}
        </div>
      </div>

      <button onClick={onSkip} style={{ position: 'fixed', top: 'calc(10px + env(safe-area-inset-top,0px))', right: 12, zIndex: 3003, border: 0, background: 'rgba(255,255,255,0.92)', color: '#3A3F45', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', pointerEvents: 'auto' }}>Esci dal tutorial</button>
    </div>
  );
}

// Step 0 del mini-tutorial: scelta del PROFILO INVESTITORE (Michael 2026-08-07).
// "Rivelasta cerca gli immobili per te in base al tuo profilo": 4 archetipi
// one-tap + link al profilo avanzato (form dettagliato, in arrivo — vedi
// docs/PROFILO_INVESTITORE.md). La scelta è salvata in localStorage; il wiring
// al backend arriva con l'implementazione del match per-profilo.
const ARCHETIPI_INVESTITORE = [
  { id: 'flipper',     emoji: '🔨', nome: 'Il Flipper',     desc: 'Compro, sistemo, rivendo. Ritorno entro 12-18 mesi.' },
  { id: 'cassettista', emoji: '🏦', nome: 'Il Cassettista', desc: 'Compro e metto a reddito. Conta la rendita mensile.' },
  { id: 'costruttore', emoji: '🏗️', nome: 'Il Costruttore', desc: 'Cerco l’operazione: frazionamenti, cantieri, margini alti.' },
  { id: 'prudente',    emoji: '🛡️', nome: 'Il Prudente',    desc: 'Un buon affare senza sorprese, anche come prima casa.' },
];

// Mappa archetipo → dimensioni note del profilo avanzato: se apri l'avanzato dopo aver
// toccato un archetipo, il form parte pre-compilato con le sue scelte (Michael 2026-08-10).
const ARCHETIPO_DETTAGLI = {
  flipper:     { obiettivo: 'rivendita', regime_acquisto: 'seconda_casa' },
  cassettista: { obiettivo: 'reddito',   regime_acquisto: 'seconda_casa' },
  costruttore: { obiettivo: 'rivendita', regime_acquisto: 'azienda', impegno: 'imprenditoriale' },
  prudente:    { obiettivo: 'rivendita', regime_acquisto: 'prima_casa' },
};

function ProfiloInvestitoreStep({ onPick, onSkip, onExit }) {
  // Modalità avanzata: il form completo delle 7 grandezze (Michael 2026-08-07).
  const [advanced, setAdvanced] = useState(false);
  // Scelta salvata in precedenza → evidenziata (lo step si mostra SEMPRE).
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem('rivelasta_profilo_investitore') || 'null'); } catch { /* noop */ }
  // Precompilazione del form avanzato (Michael 2026-08-10): profilo personalizzato salvato →
  // altrimenti la BOZZA parziale (scelte premute e non ancora salvate) → altrimenti l'eredità
  // dall'archetipo toccato. Così riapri l'avanzato e RIVEDI selezionato ciò che avevi premuto.
  let draftAvanzato = null;
  try { draftAvanzato = JSON.parse(localStorage.getItem('rivelasta_profilo_avanzato_draft') || 'null'); } catch { /* noop */ }
  const advInitial = saved?.dettagli || draftAvanzato || (saved?.archetipo && ARCHETIPO_DETTAGLI[saved.archetipo]) || null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, fontFamily: 'Manrope, system-ui, sans-serif' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.62)', pointerEvents: 'auto' }} />
      {/* paddingTop tiene la card SOTTO il tastino "Esci dal tutorial" (fixed in
          alto a dx): niente sovrapposizione anche su schermi bassi. 2026-08-07. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 3002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px', paddingTop: 'calc(52px + env(safe-area-inset-top, 0px))', pointerEvents: 'none' }}>
        <div style={{ maxWidth: 380, width: '100%', maxHeight: 'calc(100vh - 80px - env(safe-area-inset-top, 0px))', overflowY: 'auto', background: '#fff', color: '#1F2937', borderRadius: 18, padding: '20px 18px', boxShadow: '0 16px 44px rgba(0,0,0,0.4)', pointerEvents: 'auto', animation: 'tutIn .28s ease-out both' }}>
          {advanced ? (
            <ProfiloInvestitoreForm
              initial={advInitial}
              onBack={() => setAdvanced(false)}
              onSave={(dettagli) => onPick({ archetipo: 'personalizzato', dettagli })}
            />
          ) : (
            <>
              <div style={{ fontSize: 19, fontWeight: 900, lineHeight: 1.25, letterSpacing: '-0.01em' }}>Rivelasta cerca gli immobili per te</div>
              <div style={{ marginTop: 6, fontSize: 13.5, color: '#6B7280', lineHeight: 1.45 }}>
                Dimmi che investitore sei: il verdetto di ogni asta sarà calibrato sul <b>tuo</b> profilo.
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {ARCHETIPI_INVESTITORE.map((a) => {
                  const active = saved?.archetipo === a.id;
                  return (
                    <button key={a.id} onClick={() => onPick({ archetipo: a.id })} style={{
                      display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                      background: active ? '#EAF2FF' : '#F6F7F9',
                      border: active ? '1.5px solid #1F6FEB' : '1.5px solid #EEF0F2',
                      borderRadius: 14, padding: '12px 13px', cursor: 'pointer',
                    }}>
                      <span style={{ fontSize: 26, lineHeight: 1 }}>{a.emoji}</span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 15, fontWeight: 800 }}>{a.nome}{active ? ' · attuale' : ''}</span>
                        <span style={{ display: 'block', fontSize: 12.5, color: '#6B7280', lineHeight: 1.35, marginTop: 2 }}>{a.desc}</span>
                      </span>
                      <span style={{ color: '#1F6FEB', fontWeight: 800, fontSize: 16 }}>›</span>
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setAdvanced(true)} style={{ marginTop: 12, width: '100%', border: 0, background: 'transparent', color: '#1F6FEB', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', padding: 6 }}>
                {saved?.archetipo === 'personalizzato' ? 'Modifica il tuo profilo avanzato ›' : 'Imposta profilo avanzato ›'}
              </button>
              <button onClick={onSkip} style={{ marginTop: 8, width: '100%', border: 0, background: 'transparent', color: '#9CA3AF', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: 6 }}>
                {saved ? 'Continua senza modifiche' : 'Salta per ora'}
              </button>
            </>
          )}
        </div>
      </div>
      <button onClick={onExit} style={{ position: 'fixed', top: 'calc(10px + env(safe-area-inset-top,0px))', right: 12, zIndex: 3003, border: 0, background: 'rgba(255,255,255,0.92)', color: '#3A3F45', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', pointerEvents: 'auto' }}>Esci dal tutorial</button>
      <style>{'@keyframes tutIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
    </div>
  );
}

function loadGoogle() {
  if (!googleLoadPromise) {
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!key) {
      googleLoadPromise = Promise.reject(new Error('VITE_GOOGLE_MAPS_API_KEY non configurata'));
    } else {
      setOptions({ key, v: 'weekly' });
      // Pre-carica core, maps, marker, streetView per istanziare la mappa al render
      googleLoadPromise = Promise.all([
        importLibrary('core'),
        importLibrary('maps'),
        importLibrary('marker'),
        importLibrary('streetView'),
      ]).then(() => window.google);
    }
  }
  return googleLoadPromise;
}

// Parametri "salvabili" di una ricerca (filtri + zona). Esclude i transitori
// (sid, scanning, focus, view). center_label è cosmetico → fuori dal confronto.
const SAVEABLE_KEYS = [
  'verdetto', 'prezzo_max', 'scade_entro', 'mq_min', 'mq_max', 'tipologia_categoria',
  'comune', 'tribunale', 'piano_categoria', 'livello_ristrutturazione',
  'tentativo_vendita', 'disponibilita', 'center_lat', 'center_lng', 'center_label', 'area', 'vp',
];
const extractSaveable = (sp) => {
  const o = {};
  SAVEABLE_KEYS.forEach((k) => { const v = sp.get(k); if (v != null && v !== '') o[k] = v; });
  return o;
};
// Firma di confronto (ignora center_label, che può cambiare per motivi cosmetici).
const saveSignature = (obj) => {
  const o = { ...obj }; delete o.center_label;
  return JSON.stringify(o);
};

export default function Mappa() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isProspect = !!user?.is_prospect;
  const outletCtx = useOutletContext() || {};
  const toggleSidebar = outletCtx.toggleSidebar;
  const navigate = useNavigate();

  // === Modifica ricerca salvata: se arrivo con ?sid, e cambio un filtro, appare
  // "Salva le modifiche". Marina 2026-07-12. ===
  const editSid = searchParams.get('sid');
  // Soglia "nuovi": immobili creati dopo questo istante sono evidenziati in azzurro.
  const sinceTs = (() => {
    const s = searchParams.get('since');
    const t = s ? Date.parse(s) : NaN;
    return Number.isFinite(t) ? t : null;
  })();
  const savedBaselineRef = useRef(null);
  const [saveDirty, setSaveDirty] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  useEffect(() => {
    if (!editSid) { savedBaselineRef.current = null; setSaveDirty(false); return; }
    if (savedBaselineRef.current === null) {
      // primo caricamento della ricerca → baseline = params attuali (dell'URL)
      savedBaselineRef.current = extractSaveable(new URLSearchParams(window.location.search));
      setSaveDirty(false);
      return;
    }
    setSaveDirty(saveSignature(extractSaveable(searchParams)) !== saveSignature(savedBaselineRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSid, searchParams.toString()]);
  const saveSearchChanges = async () => {
    if (!editSid || savingSearch) return;
    setSavingSearch(true);
    const params = extractSaveable(searchParams);
    try {
      await updateSavedSearch(editSid, { params });
      savedBaselineRef.current = params;
      setSaveDirty(false);
    } catch (_) { /* noop */ } finally { setSavingSearch(false); }
  };
  const [hint, setHint] = useState(null); // 'intro' | 'ottimi' | null
  const [popupOpen, setPopupOpen] = useState(false); // InfoWindow aperto (per modal prospect)
  const [omiRow, setOmiRow] = useState(null); // Row target per la modale Analisi Deep OMI
  const [carousel, setCarousel] = useState(null); // rows da mostrare in carousel (prospect: tap marker o cluster)
  const [streetViewPano, setStreetViewPano] = useState(null); // {lat, lng, address} per modal in-app
  const [limitModalOpen, setLimitModalOpen] = useState(false); // modal "esaurite analisi"
  const [prospectViewedCount, setProspectViewedCount] = useState(0); // count reale dal backend (prospect_views)
  const [notifyModalOpen, setNotifyModalOpen] = useState(false); // modal "vuoi ricevere notifiche?"
  const [waNotifEnabled, setWaNotifEnabled] = useState(() => {
    try { return localStorage.getItem('pr_wa_notif') !== '0'; } catch (_) { return true; }
  });
  // Prospect: carica gli immobili GIÀ ANALIZZATI (persistenti) → restano colorati
  // col loro verdetto anche dopo un refresh, e il contatore del cap resta corretto.
  // (Marina 2026-07-11: prima si resettavano ad ogni reload — comportamento debug.)
  useEffect(() => {
    if (!isProspect) return;
    api.get('/api/prospect/analyzed')
      .then(({ data }) => {
        setProspectAnalyzedIds(new Set(data?.ids || []));
        setDiscoveredVerdetti(new Set(data?.verdetti || []));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect]);

  // Sync con cambi fatti su altre pagine (Impostazioni): storage event (cross-tab)
  // + custom event 'pr-wa-notif-change' (stesso tab).
  useEffect(() => {
    const sync = () => {
      try { setWaNotifEnabled(localStorage.getItem('pr_wa_notif') !== '0'); } catch (_) {}
    };
    window.addEventListener('storage', sync);
    window.addEventListener('pr-wa-notif-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('pr-wa-notif-change', sync);
    };
  }, []);
  const [pendingAnalysisModalOpen, setPendingAnalysisModalOpen] = useState(false); // modal "analisi richiede più tempo"
  const [emptyFilterModalOpen, setEmptyFilterModalOpen] = useState(false); // modal "filtro senza analisi"
  // Prospect onboarding "reveal": all'inizio è visibile solo il marker del primary_interest.
  // Quando l'utente chiude la card iniziale, gli altri pallini (tutti grigi/non_analizzato)
  // appaiono con drop animation. La transizione gray→colore avviene per ogni marker solo
  // dopo che l'utente avrà cliccato "Avvia analisi" sulla sua card.
  const [markersRevealed, setMarkersRevealed] = useState(false);
  // Onboarding zoom-in: la mappa parte zoomata lontano (zoom 6 = Italia), poi
  // anima fino al target (13). Solo a animazione completata appare il marker
  // primary con bounce (KF.marker del design BTC: scale .4 → 1.45 → 1).
  const [prospectZoomDone, setProspectZoomDone] = useState(false);
  const prospectZoomTriggeredRef = useRef(false);
  const entryAnimatedIdsRef = useRef(new Set()); // marker che hanno già fatto la bounce entry — non rianimare al rebuild
  // Carosello prospect: indice card attiva + stato drag (per pan + dot sync).
  // Replica esatta del meccanismo del design BTC: track con transform translateX(-idx*step+dragX),
  // non native overflow-scroll, così la card successiva fa "peek" a destra.
  const [carIndex, setCarIndex] = useState(0);
  const [carDrag, setCarDrag] = useState({ active: false, dx: 0 });
  const carDragRef = useRef({ startX: 0, pointerId: null, captured: false });
  const [prospectAnalyzedIds, setProspectAnalyzedIds] = useState(() => new Set());
  // Verdetti già scoperti dall'utente su TUTTO il DB (non solo il viewport) → serve
  // al filtro verdetto per sapere se applicarsi anche se quei pin sono fuori vista.
  const [discoveredVerdetti, setDiscoveredVerdetti] = useState(() => new Set());
  // Stato analisi attiva (una alla volta): { rowId, verdKey, phase, scanSub, score, scorePop, shown[] }
  // phase: 'scan' (1.7s, score lampeggia random) → 'accumulate' (factor cards appaiono uno per uno) → 'reveal' (flash + verdetto colorato) → null (passato a "done")
  const [prospectAn, setProspectAn] = useState(null);
  // Coriandoli reveal "ottimo": stato DISACCOPPIATO da prospectAn così i pezzi
  // completano la caduta ed escono dallo schermo naturalmente (prima venivano
  // smontati insieme a prospectAn dopo 1700ms → sparivano a metà). Marina 2026-07-11.
  const [confetti, setConfetti] = useState(false);
  const prospectAnTimers = useRef([]);
  const prospectAnFlicker = useRef(null);
  const prospectScoreInt = useRef(null);
  const clearProspectAn = () => {
    prospectAnTimers.current.forEach(clearTimeout);
    prospectAnTimers.current = [];
    if (prospectAnFlicker.current) { clearInterval(prospectAnFlicker.current); prospectAnFlicker.current = null; }
    if (prospectScoreInt.current) { clearInterval(prospectScoreInt.current); prospectScoreInt.current = null; }
    setConfetti(false);
  };
  // Pezzi coriandoli memoizzati: generati UNA volta, così i re-render durante la
  // caduta (~4s) non li rigenerano (niente scatti/restart).
  const confettiPieces = useMemo(() => {
    const cols = ['#16915E', '#4FD89E', '#F2B23E', '#2A6FDB', '#E0A800'];
    return Array.from({ length: 36 }).map((_, i) => (
      <div key={i} className="pr-confetti-piece pr-confetti-piece--fs" style={{
        left: `${Math.random() * 100}%`,
        width: `${7 + Math.random() * 6}px`,
        height: `${10 + Math.random() * 8}px`,
        background: cols[i % cols.length],
        animationDuration: `${1800 + Math.random() * 1400}ms`,
        animationDelay: `${Math.random() * 500}ms`,
      }} />
    ));
  }, []);
  useEffect(() => () => clearProspectAn(), []);

  // Reset indice quando carousel cambia (apertura/cluster diverso)
  useEffect(() => { setCarIndex(0); setCarDrag({ active: false, dx: 0 }); }, [carousel]);

  // Prospect: ricolora il cluster attivo in base alla card selezionata nel carousel.
  // - Triggerato su cambi di carousel/carIndex/stato analisi
  // - Riapplicato anche al rerender del clusterer (evento 'clusteringend'),
  //   così pan/zoom non resettano il colore mentre il carousel cluster è aperto.
  const applyActiveClusterColorRef = useRef(() => {});
  applyActiveClusterColorRef.current = () => {
    if (!isProspect) return;
    const g = googleRef.current;
    const cl = clustererRef.current;
    if (!g || !cl) return;
    if (!carousel || !carousel.length) return;
    const selRow = carousel[carIndex];
    if (!selRow) return;
    // Nota: NON restringiamo a carousel.length >= 2. La card singola del
    // primary_interest può comunque ricadere geograficamente dentro un cluster
    // di altri POI vicini; in quel caso vogliamo comunque colorare il cluster
    // al reveal. Il lookup `clusters.find(...)` farà no-op se il marker non
    // appartiene ad alcun cluster.
    // Il cluster cambia colore SOLO al momento del reveal del verdetto (cioè
    // contemporaneamente al cambio colore del pin singolo via prospectAnalyzedIds).
    // Durante scan/accumulate resta grigio: niente "anticipo" del verdetto.
    const isRevealed = prospectAnalyzedIds.has(selRow.id)
      || (prospectAn && prospectAn.rowId === selRow.id && prospectAn.phase === 'reveal');
    const selMk = markersRef.current.find((m) => m && m._immobileId === selRow.id);
    if (!selMk) return;
    const clusters = cl.clusters || [];
    const active = clusters.find((c) => c.markers && c.markers.includes(selMk));
    if (!active || !active.marker) return;
    // ⚠ Marina 2026-07-11: con AdvancedMarkerElement `cl.clusters` include anche i
    // SINGOLETTI (cluster di 1), dove `active.marker` È il marker singolo. Senza
    // questo guard ricoloravamo il pin singolo con l'icona-cluster → "diventava
    // grande con 1 dentro". Ricoloriamo SOLO i cluster veri (2+ marker).
    if (active.markers.length < 2) return;
    // Ricostruisci la torta del cluster includendo l'immobile appena rivelato
    // (il cui _category potrebbe non essere ancora aggiornato in questo tick).
    const override = isRevealed ? { id: selRow.id, cat: selRow.rivelasta_verdetto || 'non_analizzato' } : null;
    const counts = prospectClusterCounts(active.markers, override);
    const icon = prospectClusterHasResult(counts)
      ? buildProspectClusterPie(g, counts, active.markers.length)
      : buildProspectClusterIcon(g, PROSPECT_VERDETTO_COLORS.null, active.markers.length);
    try { active.marker.content = iconToContent(icon); } catch (_) {}
  };
  useEffect(() => {
    applyActiveClusterColorRef.current();
  }, [isProspect, carousel, carIndex, prospectAnalyzedIds, prospectAn?.cfgKey, prospectAn?.phase, prospectAn?.rowId]);

  // Prospect: highlight del marker selezionato — UN solo overlay HTML che segue
  // il marker via projection. Animazione pulse in CSS keyframes (GPU compositor),
  // niente SMIL, niente per-marker work. Costo costante = 1 DOM node.
  const haloListenerRef = useRef(null);
  const prospectHaloOverlayRef = useRef(null);
  const rowsRefForCluster = useRef([]); // accesso a rows correnti dentro il closure onClusterClick (clusterer non viene ricreato)
  useEffect(() => {
    if (!isProspect) return;
    const g = googleRef.current;
    const m = mapRef.current;
    if (!g || !m) return;
    const selectedId = carousel && carousel[carIndex] ? carousel[carIndex].id : null;

    // Istanzia (una volta) l'OverlayView dell'halo nel pane overlayLayer
    if (!prospectHaloOverlayRef.current) {
      const Cls = getProspectHaloOverlayClass(g);
      prospectHaloOverlayRef.current = new Cls();
      prospectHaloOverlayRef.current.setMap(m);
    }
    const haloOv = prospectHaloOverlayRef.current;

    if (selectedId == null) {
      haloOv.setActive(false);
      return;
    }
    const next = markersRef.current.find((mk) => mk && mk._immobileId === selectedId);
    if (!next) return;
    const focal = prospectFocalLatLngRef.current;
    const haloPos = focal
      ? new g.maps.LatLng(focal.lat, focal.lng)
      : new g.maps.LatLng(mLL(next));
    haloOv.setColor(next._prospectColor || '#999');
    haloOv.setLatLng(haloPos);
    haloOv.setActive(true);
  }, [isProspect, carousel, carIndex]);

  // Avvia macchina a stati 'scan' → 'accumulate' → 'reveal' → done.
  // - scan (1.7s): score lampeggia random per dare effetto "sto leggendo"
  // - accumulate: i fattori della VC appaiono uno alla volta (920ms cadenza), score si anima per ognuno
  // - lockScore: pop finale sul punteggio
  // - reveal: card si tinta del colore del verdetto, pill colorato, flash bianco
  const PROSPECT_ANALYSIS_LIMIT = 3; // DEBUG: temporaneo, allineato col backend

  // Salva/rimuovi dai preferiti dalla card prospect (cuore nel box verdetto).
  const toggleProspectFav = async (row) => {
    const nv = !row.preferito;
    patchRow(row.id, { preferito: nv });
    setCarousel((c) => (c ? c.map((x) => (x.id === row.id ? { ...x, preferito: nv } : x)) : c));
    try { await apiTogglePreferito(row.id); } catch (_) {
      patchRow(row.id, { preferito: !nv });
      setCarousel((c) => (c ? c.map((x) => (x.id === row.id ? { ...x, preferito: !nv } : x)) : c));
    }
  };
  const prospectPollTimers = useRef({});      // id → intervalId dei poll analisi
  const prospectOpenCardIdRef = useRef(null); // id immobile con card aperta ora

  // Animazione scan→accumulate→reveal per un immobile CON verdetto già noto.
  const runProspectRevealAnimation = (row) => {
    const verdKey = row.rivelasta_verdetto;
    const cfgKey = PROSPECT_VC_KEY(verdKey);
    const cfg = PROSPECT_VC[cfgKey];
    clearProspectAn();
    setProspectAn({ rowId: row.id, cfgKey, phase: 'scan', scanSub: 0, score: 0, scorePop: 1, shown: [] });
    prospectAnFlicker.current = setInterval(() => {
      setProspectAn((s) => s && s.phase === 'scan' ? { ...s, score: Math.floor(Math.random() * 880) + 60 } : s);
    }, 70);
    const T = (fn, ms) => { const id = setTimeout(fn, ms); prospectAnTimers.current.push(id); };
    T(() => setProspectAn((s) => s ? { ...s, scanSub: 1 } : s), 900);
    T(() => setProspectAn((s) => s ? { ...s, scanSub: 2 } : s), 1700);
    T(() => {
      if (prospectAnFlicker.current) { clearInterval(prospectAnFlicker.current); prospectAnFlicker.current = null; }
      setProspectAn((s) => s ? { ...s, phase: 'accumulate', score: 0 } : s);
      const revealFactor = (idx) => {
        const f = cfg.factors[idx];
        setProspectAn((s) => s ? { ...s, shown: [...s.shown, { icon: f[0], label: f[1], pts: f[2] }], scorePop: 1.22 } : s);
        T(() => setProspectAn((s) => s ? { ...s, scorePop: 1 } : s), 150);
        const from = (() => { let acc = 0; for (let k = 0; k < idx; k++) acc += cfg.factors[k][2]; return acc; })();
        const to = from + f[2];
        if (prospectScoreInt.current) clearInterval(prospectScoreInt.current);
        const t0 = Date.now(), dur = 640;
        prospectScoreInt.current = setInterval(() => {
          const p = Math.min(1, (Date.now() - t0) / dur);
          const e = 1 - Math.pow(1 - p, 3);
          const val = Math.round(from + (to - from) * e);
          setProspectAn((s) => s ? { ...s, score: val } : s);
          if (p >= 1) { clearInterval(prospectScoreInt.current); prospectScoreInt.current = null; }
        }, 16);
        if (idx + 1 < cfg.factors.length) T(() => revealFactor(idx + 1), 920);
        else T(() => {
          setProspectAn((s) => s ? { ...s, scorePop: 1.36 } : s);
          T(() => setProspectAn((s) => s ? { ...s, scorePop: 1 } : s), 200);
          T(() => {
            setProspectAn((s) => s ? { ...s, phase: 'reveal', score: cfg.target } : s);
            setProspectAnalyzedIds((s) => new Set([...s, row.id]));
            if (row.rivelasta_verdetto) setDiscoveredVerdetti((s) => new Set([...s, row.rivelasta_verdetto]));
            if (cfgKey === 'ottimo') { setConfetti(true); T(() => setConfetti(false), 4200); }
            T(() => setProspectAn(null), 1700);
          }, 440);
        }, 1000);
      };
      revealFactor(0);
    }, 2400);
  };

  // Poll dopo una richiesta di analisi: quando il job è pronto, se stai guardando
  // quella card parte l'animazione del verdetto (in diretta); altrimenti aggiorna
  // solo il marker. Lo stato "in analisi" è già persistente via job_status backend.
  const startProspectAnalysisPoll = (id) => {
    if (prospectPollTimers.current[id]) return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const s = await astemasterStatus(id);
        if (s && s.status === 'done' && s.rivelasta_verdetto) {
          clearInterval(iv); delete prospectPollTimers.current[id];
          patchRow(id, { job_status: null, url_report: s.url_report, rivelasta_verdetto: s.rivelasta_verdetto });
          if (prospectOpenCardIdRef.current === id) {
            runProspectRevealAnimation({ id, rivelasta_verdetto: s.rivelasta_verdetto });
          } else {
            setProspectAnalyzedIds((set) => new Set([...set, id]));
            setDiscoveredVerdetti((set) => new Set([...set, s.rivelasta_verdetto]));
          }
        } else if ((s && s.status === 'error') || tries > 180) {
          clearInterval(iv); delete prospectPollTimers.current[id];
        }
      } catch (_) {
        if (tries > 180) { clearInterval(iv); delete prospectPollTimers.current[id]; }
      }
    }, 5000);
    prospectPollTimers.current[id] = iv;
  };

  const tutorialActiveRef = useRef(false);   // true mentre il mini-tutorial è in corso
  const tutorialEndReportRef = useRef(false); // true = report aperto a FINE tutorial → chiudendolo torna alla Home
  const startProspectAnalysis = async (row) => {
    // Mini-tutorial: solo animazione di reveal, SENZA registrare/consumare il cap
    // (così è ripetibile all'infinito). Robustо: usiamo un ref, non solo l'URL.
    if (tutorialActiveRef.current || searchParams.get('tutorial') === '1') { runProspectRevealAnimation(row); return 'external'; }
    // Immobile non ancora analizzato: AVVIA davvero l'analisi (job astemaster) +
    // registra la richiesta → il verdetto arriverà sul WhatsApp del prospect.
    // Marina/Michael 2026-07-14: niente radar toast immediato — la CARD fa partire
    // l'animazione, la barra si BLOCCA e compare il fumetto "ci vuole più tempo".
    if (!row.rivelasta_verdetto) {
      try {
        const { data } = await api.post(`/api/immobili/${row.id}/prospect-request-analysis`);
        if (typeof data?.viewed === 'number') setProspectViewedCount(data.viewed);
      } catch (e) {
        if (e?.response?.status === 403) {
          const v = e?.response?.data?.viewed;
          if (typeof v === 'number') setProspectViewedCount(v);
          setLimitModalOpen(true);
          return false;
        }
        // altri errori: fail-open, procediamo con l'animazione (best-effort)
      }
      setRows((rs) => rs.map((x) => x.id === row.id ? { ...x, job_status: 'queued' } : x));
      setCarousel((c) => (c ? c.map((x) => (x.id === row.id ? { ...x, job_status: 'queued' } : x)) : c));
      // Marker → "in analisi" (spinner) SUBITO, senza attendere un refetch.
      const mk = markersRef.current.find((m) => m._immobileId === row.id);
      if (mk && googleRef.current) {
        mk._category = 'in_analisi';
        mk._prospectColor = PROSPECT_VERDETTO_COLORS.in_analisi;
        mk.content = buildProspectAnalyzingContent(PROSPECT_VERDETTO_COLORS.in_analisi);
      }
      startProspectAnalysisPoll(row.id);
      return 'requested';
    }
    // Immobile GIÀ analizzato (ha verdetto): stiamo solo APRENDO il verdetto, non
    // avviando una nuova analisi. Marina/Michael 2026-07-14: NIENTE modale del cap
    // qui — il cap vale solo sull'AVVIO di una nuova analisi (ramo sopra).
    // L'animazione parte ISTANTANEAMENTE al tap; la POST di registrazione vista è
    // best-effort in background (non deve introdurre delay). Marina 2026-07-14.
    runProspectRevealAnimation(row);
    api.post(`/api/immobili/${row.id}/prospect-analyze`)
      .then(({ data }) => { if (typeof data?.viewed === 'number') setProspectViewedCount(data.viewed); })
      .catch((e) => { const v = e?.response?.data?.viewed; if (typeof v === 'number') setProspectViewedCount(v); });
    return 'external';
  };

  // Traccia quale card prospect è aperta e (ri)avvia il poll se quell'immobile è
  // in analisi (così restando sulla card vedi il reveal appena il job finisce).
  useEffect(() => {
    const openRow = (isProspect && carousel && carousel[carIndex]) ? carousel[carIndex] : null;
    prospectOpenCardIdRef.current = openRow ? openRow.id : null;
    if (openRow && !openRow.rivelasta_verdetto && (openRow.job_status === 'queued' || openRow.job_status === 'running')) {
      startProspectAnalysisPoll(openRow.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, carousel, carIndex]);

  // Cleanup dei poll all'unmount.
  useEffect(() => () => { Object.values(prospectPollTimers.current).forEach((iv) => clearInterval(iv)); }, []);

  const [markerHintPos, setMarkerHintPos] = useState(null); // {x,y} pixel di un marker per ancorare il tooltip "tocca un pallino"
  // Stato temporaneo del campo indirizzo: al focus svuota, al blur ripristina se non c'è stata una selezione
  const [addrDraft, setAddrDraft] = useState(null); // null = mostra il valore reale dell'URL
  const [lastSelectedCoords, setLastSelectedCoords] = useState(null); // {lat,lng} dell'indirizzo selezionato (per il bottone "vai a")
  const prospectInitialCenteredRef = useRef(false); // true dopo che il prospect è stato centrato sull'immobile di interesse
  const prospectAutoCarouselRef = useRef(false);    // true dopo l'auto-apertura della card del primary_interest
  const [radarOn, setRadarOn] = useState(false); // Radar: filtro di ricerca personalizzato del prospect
  const [radarSheetOpen, setRadarSheetOpen] = useState(false);
  const [radarPhone, setRadarPhone] = useState(null);
  const [radarForm, setRadarForm] = useState({ prezzo_max: '', tipologia: '', comune: '' });
  const [radarToast, setRadarToast] = useState(null);
  const [reportUrl, setReportUrl] = useState(null); // URL del report da mostrare in modale iframe
  // Chiude il modale report. Eccezione: se è il report aperto a FINE tutorial,
  // chiudendolo si torna alla Home (il tutorial è concluso). Marina 2026-07-14.
  const closeReport = () => {
    setReportUrl(null);
    if (tutorialEndReportRef.current) {
      tutorialEndReportRef.current = false;
      navigate('/app/home');
    }
  };
  const overlayViewRef = useRef(null);

  // Prospect: niente tooltip onboarding "Inizia da qui" — flusso diretto sulla card
  // (l'auto-apertura del carousel su primary_interest sta DOPO la dichiarazione di rows)

  // Carica stato radar al mount del prospect
  useEffect(() => {
    if (!isProspect) return;
    fetchProspectRadar().then((d) => {
      setRadarOn(!!d.active);
      setRadarPhone(d.phone || null);
      setRadarForm({
        prezzo_max: d.criteria?.prezzo_max ?? '',
        tipologia: d.criteria?.tipologia_categoria ?? '',
        comune: d.criteria?.comune ?? '',
      });
    }).catch(() => {});
  }, [isProspect]);

  // Quando passo a hint='ottimi', proietta la posizione di un marker visibile per ancorare il tooltip
  useEffect(() => {
    if (!isProspect || hint !== 'ottimi' || !mapRef.current || !googleRef.current) {
      setMarkerHintPos(null);
      return;
    }
    const google = googleRef.current;
    const map = mapRef.current;
    if (!overlayViewRef.current) {
      const ov = new google.maps.OverlayView();
      ov.onAdd = () => {}; ov.draw = () => {}; ov.onRemove = () => {};
      ov.setMap(map);
      overlayViewRef.current = ov;
    }
    const ov = overlayViewRef.current;
    let locked = false, attempts = 0;
    const tryProject = () => {
      if (locked || attempts > 30) return;
      attempts++;
      const proj = ov.getProjection();
      if (!proj) return;
      const target = markersRef.current.find((m) => m.map === map);
      if (!target) return;
      const px = proj.fromLatLngToContainerPixel(new googleRef.current.maps.LatLng(mLL(target)));
      if (!px || (px.x === 0 && px.y === 0)) return;
      locked = true;
      setMarkerHintPos({ x: px.x, y: px.y });
    };
    const interval = setInterval(tryProject, 60);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hint, isProspect]);



  // [RIMOSSO 2026-07-12] Vecchio effetto B2C che cancellava center_lat/center_lng/
  // radius_km dall'URL per i prospect: sabotava la centratura sulla zona della
  // ricerca (la mappa restava sull'Italia). center_lat non è un filtro server-side
  // (il fetch usa il bbox), quindi tenerlo nell'URL è corretto e necessario.

  // [RIMOSSO] Vecchio effect "set center+zoom 13" che faceva un flash di
  // zoom 13 sulla via prima della nostra animazione zoom-in (6 → 13). La
  // logica di centering + zoom-in animato è ora SOLO nel marker effect.

  const view = searchParams.get('view') || 'valutazione';
  const verdetto = searchParams.get('verdetto') || '';
  // Verdetto multi-select: set di chiavi attive parsate dal CSV in URL
  const verdettoSet = useMemo(
    () => new Set(verdetto ? verdetto.split(',').map((s) => s.trim()).filter(Boolean) : []),
    [verdetto]
  );
  const toggleVerdetto = (key) => {
    const next = new Set(verdettoSet);
    if (key === '') {
      next.clear(); // "Tutti" → reset
    } else if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    updateFilter({ verdetto: next.size ? Array.from(next).join(',') : '' });
  };
  const query = searchParams.get('q') || '';
  const focusId = searchParams.get('focus');
  // Il centraggio sul marker del deep-link (?focus=) va fatto UNA sola volta:
  // altrimenti a ogni re-render/refetch la mappa "rimbalza" indietro sul punto
  // e non ti lascia navigare (bug Marina 2026-07-11).
  const focusDoneRef = useRef(false);
  useEffect(() => { focusDoneRef.current = false; }, [focusId]);

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const infoWindowRef = useRef(null);
  const googleRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [scanningZone, setScanningZone] = useState(false); // banner "scansione PVP in corso"
  const [reloadNonce, setReloadNonce] = useState(0);       // forza refetch a fine scan
  const [totalCount, setTotalCount] = useState(0); // totale reale (dal backend), indipendente dal viewport

  // Viewport loading (Marina 2026-07-11): carica i marker solo per l'area visibile
  // (+ margine), non tutti i ~3700. bbox = "w,s,e,n" dell'area caricata.
  const [bbox, setBbox] = useState(null);
  const loadedBoundsRef = useRef(null);   // {w,s,e,n} con margine già caricato
  const loadedZoomRef = useRef(null);
  const shouldFitRef = useRef(true);      // fitBounds SOLO sui cambi filtro/primo load, MAI sui pan/zoom (evita loop)
  const lastSearchRef = useRef(null);

  // Prospect: centra il marker nello spazio libero tra filtri (in alto) e card
  // (in basso). Misura i bordi VISIBILI (esclude padding gradiente trasparente)
  // e fa panBy della delta tra centro viewport e centro rettangolo libero.
  const prospectFocalLatLngRef = useRef(null); // {lat, lng} se la sorgente è un cluster
  const centerProspectMarkerBetweenChrome = (rowOrLatLng) => {
    const m = mapRef.current;
    const g = googleRef.current;
    if (!m || !g || !rowOrLatLng) return;
    const lat = rowOrLatLng.lat, lng = rowOrLatLng.lng;
    if (lat == null || lng == null) return;
    const row = { lat, lng };
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const topEl  = document.querySelector('.pr-filters') || document.querySelector('.pr-topbar');
      const wrapEl = document.querySelector('.pr-carousel-wrap');
      const vh = window.innerHeight;
      const topBottom = topEl ? topEl.getBoundingClientRect().bottom : 0;
      const wrapH = wrapEl ? wrapEl.offsetHeight : 0;
      const wrapPadTop = wrapEl ? parseFloat(getComputedStyle(wrapEl).paddingTop) || 0 : 0;
      const cardTop = (vh - wrapH) + wrapPadTop;
      const desiredY = topBottom + (cardTop - topBottom) / 2;
      const offsetPx = (vh / 2) - desiredY;
      // Converti l'offset px in delta lat tramite la projection del mondo Google
      // → calcola un singolo target latlng e usa panTo per un'animazione fluida.
      const proj = m.getProjection();
      if (!proj) { m.panTo({ lat: row.lat, lng: row.lng }); return; }
      const scale = Math.pow(2, m.getZoom());
      const wp = proj.fromLatLngToPoint(new g.maps.LatLng(row.lat, row.lng));
      const target = proj.fromPointToLatLng(
        new g.maps.Point(wp.x, wp.y + offsetPx / scale)
      );
      m.panTo(target);
    }));
  };

  // Prospect: auto-apre il carousel sull'immobile preselezionato quando le rows arrivano
  useEffect(() => {
    if (!isProspect || prospectAutoCarouselRef.current) return;
    if (!prospectZoomDone) return; // attendi che lo zoom-in finisca prima di aprire la card
    const pi = user?.primary_interest;
    if (!pi || !rows.length) return;
    let target = null;
    if (pi.immobile_id) target = rows.find((r) => r.id === pi.immobile_id);
    if (!target) {
      const d = (a, b) => Math.abs((a ?? 0) - (b ?? 0));
      target = rows.find((r) => r.lat && r.lng && d(r.lat, pi.lat) < 0.0005 && d(r.lng, pi.lng) < 0.0005);
    }
    if (target) {
      prospectAutoCarouselRef.current = true;
      // Delay esteso (~1s) dopo l'apparizione dei pin prima della card: l'occhio
      // ha tempo di percepire separatamente "i poi sono spuntati" e "ora si apre
      // la card", invece di vedere tutto sovrapposto.
      setTimeout(() => {
        // Se il marker primary è dentro un cluster, apri il carousel con TUTTI
        // gli immobili del cluster (primary come prima card), e centra sul
        // centroide del cluster (dove sta l'icona visibile) — stessa logica del
        // click su cluster, evitando l'inconsistenza fra auto-open e click.
        const tMarker = markersRef.current.find((m) => m && m._immobileId === target.id);
        const cl = clustererRef.current;
        let rowsToOpen = [target];
        let focal = null;
        if (tMarker && cl && cl.clusters) {
          const containing = cl.clusters.find((c) => c.markers && c.markers.includes(tMarker));
          if (containing && containing.markers.length > 1) {
            const ids = containing.markers.map((m) => m._immobileId).filter(Boolean);
            const idToRow = new Map(rows.map((r) => [r.id, r]));
            const allRows = ids.map((id) => idToRow.get(id)).filter(Boolean);
            // Primary in prima posizione
            const primaryIdx = allRows.findIndex((r) => r.id === target.id);
            if (primaryIdx > 0) {
              const [p] = allRows.splice(primaryIdx, 1);
              allRows.unshift(p);
            }
            rowsToOpen = allRows;
            if (containing.position) {
              focal = { lat: containing.position.lat(), lng: containing.position.lng() };
            }
          }
        }
        prospectFocalLatLngRef.current = focal;
        setCarousel(rowsToOpen);
        centerProspectMarkerBetweenChrome(focal || target);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, rows, user?.primary_interest, prospectZoomDone]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});

  // === Scelta zona (B2C): sheet + disegno area sulla mappa ===
  const [zoneSheetOpen, setZoneSheetOpen] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');
  const [drawArm, setDrawArm] = useState(false);      // "armato": mappa ancora libera, in attesa del tasto Disegna
  const [drawMode, setDrawMode] = useState(false);    // disegno attivo: mappa bloccata, si traccia
  const [drawnArea, setDrawnArea] = useState(null);   // { path:[{lat,lng}], label }
  const drawnPathRef = useRef(null);                   // usato nel filtro marker (no re-render)
  const projOverlayRef = useRef(null);                 // OverlayView per proiezione pixel→latlng
  const drawPolyRef = useRef(null);                    // Polygon committato disegnato sulla mappa

  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const activeFilterCount = countActiveFilters(searchParams);

  // ESC esce da fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Init Google Map (una sola volta)
  useEffect(() => {
    let alive = true;
    loadGoogle()
      .then((google) => {
        if (!alive || !containerRef.current) return;
        googleRef.current = google;
        // Vista iniziale: se il deep-link ha le coordinate, la mappa NASCE già
        // sull'immobile (niente lampo-Italia). Marina 2026-07-11.
        const _fLat = parseFloat(searchParams.get('lat'));
        const _fLng = parseFloat(searchParams.get('lng'));
        const _hasFocusCoords = focusId && Number.isFinite(_fLat) && Number.isFinite(_fLng);
        const map = new google.maps.Map(containerRef.current, {
          center: _hasFocusCoords ? { lat: _fLat, lng: _fLng } : { lat: 41.9028, lng: 12.4964 }, // Roma
          zoom: _hasFocusCoords ? 16 : 6,
          // Map ID → abilita AdvancedMarkerElement (marker moderni/efficienti).
          // ⚠ Con un mapId gli `styles` inline sono ignorati: gli stili (nascondi POI)
          // vanno definiti come "cloud style" associato al Map ID nella Console.
          mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined,
          mapTypeControl: false,           // tolto: usavo i due bottoni mappa/satellite duplicati
          streetViewControl: false,        // pegman nascosto (Street View resta accessibile dal popup del marker via openStreetView)
          fullscreenControl: false,        // ho già il mio fullscreen toggle
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          gestureHandling: 'greedy',       // scroll su mobile e desktop senza ctrl
          // [TEST] Mappa B2C a colori (default Google) ma con POI business
          // (hotel, B&B, ristoranti, negozi, ecc.) nascosti per non distrarre.
          styles: isProspect ? [
            { featureType: 'poi.business',         stylers: [{ visibility: 'off' }] },
            { featureType: 'poi.attraction',       stylers: [{ visibility: 'off' }] },
            { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
            { featureType: 'poi.sports_complex',   stylers: [{ visibility: 'off' }] },
            { featureType: 'transit.station',      stylers: [{ visibility: 'off' }] },
          ] : undefined,
          // Prospect: niente camera controls, zoom, rotate, scale, keyboard shortcuts
          ...(isProspect ? {
            cameraControl: false,
            zoomControl: false,
            rotateControl: false,
            scaleControl: false,
            keyboardShortcuts: false,
          } : {}),
        });
        mapRef.current = map;
        infoWindowRef.current = new google.maps.InfoWindow({ maxWidth: 320 });
        infoWindowRef.current.addListener('closeclick', () => setPopupOpen(false));
        // Click sulla mappa (fuori dal popup) → chiude
        map.addListener('click', () => setPopupOpen(false));
        // [DEBUG TEMP Marina 2026-07-11] misura quanti marker restano "sulla mappa"
        // a ogni idle: se onMap resta alto anche zoomando = i pin fuori vista NON
        // vengono tolti (ipotesi Michael). Da rimuovere dopo la diagnosi.
        map.addListener('idle', () => {
          try {
            const loaded = markersRef.current.length;
            const onMap = markersRef.current.filter((mk) => mk && mk.map).length;
            const clusters = clustererRef.current?.clusters?.length ?? 0;
            // eslint-disable-next-line no-console
            console.log(`[MAPPA] zoom=${map.getZoom()} caricati=${loaded} sullaMappa=${onMap} cluster=${clusters} prospect=${isProspect}`);
          } catch (_) {}
        });
        setMapReady(true);
      })
      .catch((e) => {
        console.error(e);
        if (alive) setError('Errore caricamento Google Maps: ' + e.message);
      });
    return () => { alive = false; };
  }, []);

  // Trigger resize quando il container cambia dimensione (fullscreen toggle)
  useEffect(() => {
    if (!mapRef.current || !googleRef.current) return;
    const t = setTimeout(() => {
      googleRef.current.maps.event.trigger(mapRef.current, 'resize');
    }, 250);
    return () => clearTimeout(t);
  }, [fullscreen]);

  // ===== Mini-tutorial guidato "Prova l'analisi AI" (?tutorial=1) =====
  const tutorial = searchParams.get('tutorial') === '1';
  // Modalità DEMO (?demo=1): evidenzia i POI che hanno GIÀ un verdetto pronto (reveal
  // istantaneo alla pressione) con un pin più scuro, così in una demo dal vivo si
  // riconoscono a colpo d'occhio da quelli che vanno in analisi in background. Gli
  // utenti normali NON la vedono. Marina/Michael 2026-07-14.
  const demoMode = searchParams.get('demo') === '1';
  const [tutStep, setTutStep] = useState(null);   // 'profilo'|'poi'|'analyze'|'analyzing'|'discover'|'verdict'
  const [tutImm, setTutImm] = useState(null);
  const [tutRect, setTutRect] = useState(null);
  const exitTutorial = () => {
    tutorialActiveRef.current = false;
    setTutStep(null); setTutImm(null); setCarousel(null);
    navigate('/app/home');
  };
  useEffect(() => { tutorialActiveRef.current = tutorial; }, [tutorial]);
  // 0. PRIMO step: scelta del profilo investitore ("Rivelasta cerca gli immobili
  //    per te in base al tuo profilo"). Si mostra SEMPRE a ogni avvio del tutorial
  //    (Michael 2026-08-07); la scelta precedente appare evidenziata nella card.
  useEffect(() => {
    if (tutorial) setTutStep('profilo');
  }, [tutorial]);
  // 1. carica l'immobile "verde" del tutorial
  useEffect(() => {
    if (!tutorial) { setTutStep(null); setTutImm(null); return; }
    let alive = true;
    fetchTutorialImmobile().then((im) => { if (alive && im) setTutImm(im); }).catch(() => {});
    return () => { alive = false; };
  }, [tutorial]);
  // 2. mostra SOLO quel POI, centra la mappa, avvia lo step 'poi'
  useEffect(() => {
    if (!tutorial || !tutImm || !mapReady) return;
    setRows([tutImm]);
    const m = mapRef.current;
    if (m) { m.panTo({ lat: tutImm.lat, lng: tutImm.lng }); m.setZoom(16); }
    setTutStep((s) => s || 'poi');
  }, [tutorial, tutImm, mapReady]);
  // 3. avanzamento step in base alle azioni dell'utente
  useEffect(() => {
    if (!tutorial || !tutImm) return;
    const id = tutImm.id;
    if (tutStep === 'poi' && carousel && carousel.some((r) => r.id === id)) setTutStep('analyze');
    else if (tutStep === 'analyze' && prospectAn && prospectAn.rowId === id) setTutStep('analyzing');
    else if (tutStep === 'discover' && reportUrl) setTutStep('verdict');
  }, [tutorial, tutImm, tutStep, carousel, prospectAn, reportUrl]);
  // 3b. dopo il reveal (coriandoli) → step 'discover'
  useEffect(() => {
    if (tutorial && tutStep === 'analyzing' && tutImm && prospectAnalyzedIds.has(tutImm.id)) {
      const t = setTimeout(() => setTutStep('discover'), 2400);
      return () => clearTimeout(t);
    }
  }, [tutorial, tutStep, tutImm, prospectAnalyzedIds]);
  // 4. calcola il rettangolo spotlight per lo step corrente
  useEffect(() => {
    if (!tutStep || tutStep === 'verdict' || tutStep === 'profilo') { setTutRect(null); return; }
    const compute = () => {
      if (tutStep === 'analyzing') {
        // illumina l'area di caricamento (barra + spinner) della card
        const v = document.querySelector('.pr-verdict');
        if (v) { const r = v.getBoundingClientRect(); setTutRect({ x: r.left - 8, y: r.top - 8, w: r.width + 16, h: r.height + 16 }); }
        return;
      }
      if (tutStep === 'poi') {
        // posizione REALE del marker (DOM), così lo spotlight è centrato su di esso
        const mk = (markersRef.current || []).find((m) => m && m._immobileId === tutImm?.id);
        const el = mk && mk.content;
        const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
        if (r && r.width > 0) { setTutRect({ x: r.left - 8, y: r.top - 8, w: r.width + 16, h: r.height + 16, circle: false }); return; }
        const c = containerRef.current;
        if (c) { const cr = c.getBoundingClientRect(); setTutRect({ x: cr.left + cr.width / 2 - 40, y: cr.top + cr.height / 2 - 72, w: 80, h: 80, circle: true }); }
      } else {
        const sel = tutStep === 'analyze' ? '.pr-cardbtn--analyze' : '.pr-cardbtn--report';
        const b = document.querySelector(sel);
        if (b) { const r = b.getBoundingClientRect(); setTutRect({ x: r.left, y: r.top, w: r.width, h: r.height }); }
      }
    };
    const t = setTimeout(compute, 380);
    const iv = setInterval(compute, 500);   // ri-misura (card/animazione cambiano layout)
    window.addEventListener('resize', compute);
    return () => { clearTimeout(t); clearInterval(iv); window.removeEventListener('resize', compute); };
  }, [tutStep]);

  // Fetch su filter change / cambio viewport (bbox)
  useEffect(() => {
    let alive = true;
    // Durante il mini-tutorial mostriamo SOLO l'immobile guidato: niente fetch POI.
    if (searchParams.get('tutorial') === '1') { setLoading(false); return () => { alive = false; }; }
    const params = { view };
    if (verdetto) params.verdetto = verdetto;
    if (query) params.q = query;
    const passthrough = [
      'comune', 'tribunale', 'piano', 'piano_categoria', 'liquidita_tier',
      'livello_ristrutturazione', 'disponibilita', 'tentativo_vendita',
      'tipologia_categoria', 'tipologia_escludi',
      'piano_escludi', 'piano_categoria_escludi', 'disponibilita_escludi',
      'prezzo_max', 'scade_entro', 'mq_min', 'mq_max',
      'dentro_gra', 'nuovi', 'scadute', 'needs_reanalysis', 'scadenza_30gg',
      'center_lat', 'center_lng', 'radius_km',
      'only_matched',
    ];
    passthrough.forEach((k) => {
      const v = searchParams.get(k);
      if (v != null && v !== '' && v !== 'false') params[k] = v;
    });

    // Un cambio dei FILTRI (searchParams) deve ri-fittare la mappa; un cambio del
    // solo viewport (bbox) NO (altrimenti loop camera→idle→refetch).
    const sp = searchParams.toString();
    if (sp !== lastSearchRef.current) {
      shouldFitRef.current = true;
      lastSearchRef.current = sp;
    }

    // Viewport loading: carica solo l'area visibile invece di tutti i ~3700.
    // Attivo sia in B2C (Marina 2026-07-11: è lì che serve) sia operatore, TRANNE
    // quando c'è un filtro geografico (comune/tribunale/raggio) → in quel caso
    // mostriamo TUTTI i risultati del filtro e fittiamo, come prima.
    const hasGeoFilter = ['comune', 'tribunale', 'radius_km']
      .some((k) => { const v = searchParams.get(k); return v && v !== '' && v !== 'false'; });
    // Un filtro VERDETTO deve caricare TUTTI i risultati (non solo l'area visibile)
    // e ri-fittare la mappa, come nella versione B2B. Altrimenti, passando da un
    // colore all'altro, quelli fuori vista non risultano. Marina 2026-07-11.
    const hasVerdettoFilter = !!searchParams.get('verdetto');
    // Vista Preferiti: carica TUTTI i preferiti (ovunque siano), non solo l'area
    // visibile, così possiamo poi fittare la mappa su tutti insieme. Marina 2026-07-14.
    const isFavView = searchParams.get('view') === 'preferiti';
    const useBbox = !hasGeoFilter && !hasVerdettoFilter && !isFavView;
    if (useBbox) {
      // Aspetta il primo viewport (bbox) prima di caricare: evita di scaricare i
      // ~3700. In B2C il bbox iniziale è impostato attorno al primary_interest
      // (vedi effetto dedicato); nell'operatore dall'idle della mappa.
      if (!bbox) return () => { alive = false; };
      params.bbox = bbox;
    }

    setLoading(true);
    setError(null);
    fetchMappa(params)
      .then((res) => {
        if (!alive) return;
        setRows(res.data || []);
        if (typeof res.total === 'number') setTotalCount(res.total);
      })
      .catch((e) => {
        if (!alive) return;
        console.error(e);
        setError(e?.message || 'Errore caricamento mappa');
        setRows([]);
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), bbox, isProspect, reloadNonce]);

  // Tab "Preferiti" senza preferiti salvati → modale invece della mappa vuota.
  const [emptyFavOpen, setEmptyFavOpen] = useState(false);
  useEffect(() => {
    const viewPref = searchParams.get('view') === 'preferiti';
    if (!isProspect || !viewPref) { setEmptyFavOpen(false); return; }
    if (!loading && totalCount === 0) setEmptyFavOpen(true);
    else if (totalCount > 0) setEmptyFavOpen(false);
  }, [isProspect, loading, totalCount, searchParams.get('view')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Banner "scansione in corso": se la zona aperta è sotto import PVP (nuova zona
  // appena cercata), avvisa l'utente e ricarica appena lo scan finisce. Marina 2026-07-12.
  useEffect(() => {
    if (!isProspect || !mapReady) { setScanningZone(false); return; }
    // centro della zona: center_lat/lng o centroide dell'area disegnata
    let c = null;
    const cl = parseFloat(searchParams.get('center_lat'));
    const cn = parseFloat(searchParams.get('center_lng'));
    if (Number.isFinite(cl) && Number.isFinite(cn)) c = { lat: cl, lng: cn };
    else {
      const a = searchParams.get('area');
      if (a) {
        const pts = a.split(';').map((p) => p.split(',').map(Number)).filter((x) => x.length === 2 && Number.isFinite(x[0]) && Number.isFinite(x[1]));
        if (pts.length) c = { lat: pts.reduce((s, p) => s + p[0], 0) / pts.length, lng: pts.reduce((s, p) => s + p[1], 0) / pts.length };
      }
    }
    if (!c) { setScanningZone(false); return; }

    // Banner "ottimistico": se arrivo con ?scanning=1 (ricerca appena avviata),
    // lo mostro SUBITO senza aspettare che compaia il log running (~2-3s dopo).
    let sawScanning = new URLSearchParams(window.location.search).get('scanning') === '1';
    if (sawScanning) setScanningZone(true);

    let stop = false, iv = null;
    const check = () => api.get('/api/prospect/zone-status', { params: { lat: c.lat, lng: c.lng } })
      .then(({ data }) => {
        if (stop || !data) return;
        // COVERED per primo: se la zona ha già i dati (scansionata in passato), NIENTE
        // banner anche se un import gira in background. Marina 2026-07-12.
        if (data.covered) {
          setScanningZone((was) => { if (was || sawScanning) setReloadNonce((n) => n + 1); return false; });
          if (iv) { clearInterval(iv); iv = null; }
        } else if (data.scanning) {
          sawScanning = true;
          setScanningZone(true);
        } else if (! sawScanning) {
          // né coperta né in scansione, mai vista: nessun banner.
          setScanningZone(false);
        }
        // (se sawScanning ma il log non è ancora comparso, teniamo il banner: grace)
      }).catch(() => {});
    check();
    iv = setInterval(check, 7000);
    return () => { stop = true; if (iv) clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, mapReady, searchParams.get('center_lat'), searchParams.get('center_lng'), searchParams.get('area')]);

  // Viewport loading: quando la mappa si ferma (idle), se il viewport è uscito
  // dall'area già caricata (margine) o è cambiato lo zoom, aggiorna il bbox →
  // refetch dei soli marker visibili. Debounced. Operatore + B2C.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !googleRef.current) return;
    // B2C: attiva il viewport-loading SOLO dopo l'intro (zoom 6→13 + reveal), per
    // non disturbare l'animazione scriptata. Senza primary_interest non c'è intro
    // → attivo subito.
    if (isProspect && user?.primary_interest && !prospectZoomDone) return;
    const map = mapRef.current;
    const google = googleRef.current;
    let t = null;
    const onIdle = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        const b = map.getBounds();
        if (!b) return;
        const ne = b.getNorthEast(), sw = b.getSouthWest();
        const vw = { w: sw.lng(), s: sw.lat(), e: ne.lng(), n: ne.lat() };
        const zoom = map.getZoom();
        const lb = loadedBoundsRef.current;
        const contained = lb && vw.w >= lb.w && vw.s >= lb.s && vw.e <= lb.e && vw.n <= lb.n;
        // Refetch solo se il viewport è uscito dall'area caricata (pan/zoom-out).
        // Lo zoom-in resta contenuto → nessun refetch → fluido.
        if (contained) return;
        const dLat = (vw.n - vw.s) * 0.4, dLng = (vw.e - vw.w) * 0.4; // margine 40%
        const m = { w: vw.w - dLng, s: vw.s - dLat, e: vw.e + dLng, n: vw.n + dLat };
        loadedBoundsRef.current = m;
        loadedZoomRef.current = zoom;
        setBbox(`${m.w.toFixed(5)},${m.s.toFixed(5)},${m.e.toFixed(5)},${m.n.toFixed(5)}`);
      }, 400);
    };
    const listener = map.addListener('idle', onIdle);
    return () => { if (t) clearTimeout(t); google.maps.event.removeListener(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, mapReady, prospectZoomDone, user?.primary_interest]);

  // B2C: bbox iniziale attorno al primary_interest, così l'intro carica solo i
  // marker della zona (non tutti i ~3700). Dopo l'intro l'idle-listener prende
  // il controllo per pan/zoom. Marina 2026-07-11.
  useEffect(() => {
    if (!isProspect || bbox) return;
    const pi = user?.primary_interest;
    if (!pi?.lat || !pi?.lng) return;
    const d = 0.1; // ~±10-11 km attorno all'interesse (copre la vista a zoom 13 + margine)
    const m = { w: pi.lng - d, s: pi.lat - d, e: pi.lng + d, n: pi.lat + d };
    loadedBoundsRef.current = m;
    loadedZoomRef.current = 13; // zoom target dell'intro → nessun refetch subito dopo
    setBbox(`${m.w.toFixed(5)},${m.s.toFixed(5)},${m.e.toFixed(5)},${m.n.toFixed(5)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, user?.primary_interest]);

  // Deep-link ?focus=&lat=&lng= (dal messaggio WhatsApp): centra SUBITO la mappa
  // sull'immobile e carica direttamente quell'area → niente salto dal punto iniziale.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    if (!focusId || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const map = mapRef.current;
    map.setCenter({ lat, lng });
    map.setZoom(16);
    const d = 0.03;
    const m = { w: lng - d, s: lat - d, e: lng + d, n: lat + d };
    loadedBoundsRef.current = m;
    loadedZoomRef.current = 16;
    setBbox(`${m.w.toFixed(5)},${m.s.toFixed(5)},${m.e.toFixed(5)},${m.n.toFixed(5)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, focusId]);

  // Deep-link ?focus= SENZA coordinate: recupera lat/lng dell'immobile e centra
  // subito su quell'area (niente attesa del caricamento di tutti i marker).
  useEffect(() => {
    if (!mapReady || !mapRef.current || bbox || !focusId) return;
    if (Number.isFinite(parseFloat(searchParams.get('lat')))) return; // coord già presenti
    api.get(`/api/immobili/${focusId}`).then(({ data }) => {
      const im = data?.data || data;
      const la = parseFloat(im?.lat), lo = parseFloat(im?.lng);
      if (!Number.isFinite(la) || !Number.isFinite(lo) || !mapRef.current) return;
      const map = mapRef.current;
      map.setCenter({ lat: la, lng: lo });
      map.setZoom(16);
      const d = 0.03;
      const m = { w: lo - d, s: la - d, e: lo + d, n: la + d };
      loadedBoundsRef.current = m;
      loadedZoomRef.current = 16;
      setBbox(`${m.w.toFixed(5)},${m.s.toFixed(5)},${m.e.toFixed(5)},${m.n.toFixed(5)}`);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, focusId]);

  // B2C SENZA area di interesse e SENZA deep-link: geolocalizza l'utente e mostra
  // la SUA zona, allargando quel tanto che basta a includere alcuni immobili (bbox
  // degli 8 più vicini dal backend, clampato all'Italia). Se la geoloc è negata o
  // non ci sono immobili vicini → tutta Italia (mai fuori). Marina 2026-07-12.
  const geoLocateDoneRef = useRef(false);
  useEffect(() => {
    if (!isProspect || !mapReady || !mapRef.current || bbox) return;
    if (user?.primary_interest || focusId) return; // gestiti dai rispettivi effetti
    // Se arrivo con una zona/area già scelta (ricerca dalla Home), non geolocalizzo.
    // ⚠ Leggo l'URL LIVE (non la closure di searchParams, che al refresh può essere
    // stale/vuota → altrimenti la geoloc partiva lo stesso e centrava sulla posizione
    // del device invece che sulla zona cercata). Marina 2026-07-12.
    const hasUrlZone = () => {
      const sp = new URLSearchParams(window.location.search);
      return !!(sp.get('area') || sp.get('center_lat'));
    };
    if (hasUrlZone()) return;
    if (geoLocateDoneRef.current) return;
    geoLocateDoneRef.current = true;
    const map = mapRef.current;
    const g = googleRef.current;
    const ITALY = { south: 36.4, north: 47.2, west: 6.5, east: 18.6 };

    const applyBox = (box, { pad = 0.14, minSpan = 0.07 } = {}) => {
      let { south, north, west, east } = box;
      const cLat = (south + north) / 2, cLng = (west + east) / 2;
      // span minimo: non zoomare troppo se i punti sono quasi coincidenti
      if (north - south < minSpan) { south = cLat - minSpan / 2; north = cLat + minSpan / 2; }
      if (east - west < minSpan)   { west = cLng - minSpan / 2; east = cLng + minSpan / 2; }
      // padding per un po' di respiro attorno ai marker
      const pv = (north - south) * pad, ph = (east - west) * pad;
      south -= pv; north += pv; west -= ph; east += ph;
      // clamp Italia: non uscire mai dai confini
      south = Math.max(south, ITALY.south); north = Math.min(north, ITALY.north);
      west = Math.max(west, ITALY.west);   east = Math.min(east, ITALY.east);
      if (g) {
        map.fitBounds(new g.maps.LatLngBounds({ lat: south, lng: west }, { lat: north, lng: east }));
      }
      loadedBoundsRef.current = { w: west, s: south, e: east, n: north };
      loadedZoomRef.current = map.getZoom ? map.getZoom() : 10;
      setBbox(`${west.toFixed(5)},${south.toFixed(5)},${east.toFixed(5)},${north.toFixed(5)}`);
    };
    const showItaly = () => applyBox(ITALY, { pad: 0, minSpan: 0 });

    // Geolocalizzazione automatica DISABILITATA: entrava in conflitto con la zona
    // della ricerca (portava l'utente sulla propria posizione invece che sulla zona
    // cercata). Senza zona → mostra l'Italia. Marina 2026-07-12.
    showItaly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, mapReady, user?.primary_interest, searchParams.get('area'), searchParams.get('center_lat')]);

  // ============================ SCELTA ZONA ============================
  // Applica una zona scelta dall'autocomplete (place con lat/lng).
  const applyZoneFromPlace = ({ lat, lng, label, viewport }) => {
    if (drawPolyRef.current) { drawPolyRef.current.setMap(null); drawPolyRef.current = null; }
    drawnPathRef.current = null; setDrawnArea(null);
    const g = googleRef.current, map = mapRef.current;
    const next = new URLSearchParams(searchParams);
    next.set('center_lat', String(lat));
    next.set('center_lng', String(lng));
    if (label) next.set('center_label', label);
    next.delete('radius_km');
    setSearchParams(next, { replace: true });

    // Box da mostrare: usa il VIEWPORT reale del luogo (città → ampio, indirizzo →
    // stretto) con un piccolo margine; fallback a un box fisso ~città. Marina 2026-07-12.
    let box;
    if (viewport && viewport.south != null && viewport.north != null) {
      const padV = (viewport.north - viewport.south) * 0.12;
      const padH = (viewport.east - viewport.west) * 0.12;
      box = { w: viewport.west - padH, s: viewport.south - padV, e: viewport.east + padH, n: viewport.north + padV };
    } else {
      const d = 0.11; // ~città (più largo di prima, che tagliava Roma dentro il GRA)
      box = { w: lng - d, s: lat - d, e: lng + d, n: lat + d };
    }
    if (g && map) {
      map.fitBounds(new g.maps.LatLngBounds({ lat: box.s, lng: box.w }, { lat: box.n, lng: box.e }), 20);
    }
    loadedBoundsRef.current = box;
    loadedZoomRef.current = (map && map.getZoom) ? map.getZoom() : 13;
    setBbox(`${box.w.toFixed(5)},${box.s.toFixed(5)},${box.e.toFixed(5)},${box.n.toFixed(5)}`);
    setZoneSheetOpen(false);
  };

  // Commit di un'area disegnata (path in latlng): overlay + fit + bbox + filtro marker.
  const commitDrawnArea = (path) => {
    const g = googleRef.current, map = mapRef.current;
    if (!g || !map || !path || path.length < 3) return;
    if (drawPolyRef.current) { drawPolyRef.current.setMap(null); drawPolyRef.current = null; }
    drawPolyRef.current = new g.maps.Polygon({
      paths: path, map,
      strokeColor: '#1F6FEB', strokeOpacity: 0.95, strokeWeight: 2.5,
      fillColor: '#1F6FEB', fillOpacity: 0.08, clickable: false, zIndex: 5,
    });
    drawnPathRef.current = path;
    setDrawnArea({ path, label: 'Area disegnata' });
    const next = new URLSearchParams(searchParams);
    ['center_lat', 'center_lng', 'radius_km'].forEach((k) => next.delete(k));
    next.set('center_label', 'Area disegnata');
    setSearchParams(next, { replace: true });
    const b = pathBounds(path);
    map.fitBounds(new g.maps.LatLngBounds({ lat: b.s, lng: b.w }, { lat: b.n, lng: b.e }), 40);
    loadedBoundsRef.current = { w: b.w, s: b.s, e: b.e, n: b.n };
    loadedZoomRef.current = map.getZoom ? map.getZoom() : 12;
    setBbox(`${b.w.toFixed(5)},${b.s.toFixed(5)},${b.e.toFixed(5)},${b.n.toFixed(5)}`);
  };

  // Rimuove qualsiasi zona/area attiva.
  const clearZone = () => {
    if (drawPolyRef.current) { drawPolyRef.current.setMap(null); drawPolyRef.current = null; }
    drawnPathRef.current = null; setDrawnArea(null);
    const next = new URLSearchParams(searchParams);
    ['center_lat', 'center_lng', 'radius_km', 'center_label'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    setZoneSheetOpen(false);
  };

  // "Mappa" dalla tab bar: apre con ?around=1 (istantaneo) e geolocalizza QUI in
  // background, poi rimpiazza l'URL con center_lat/lng/vp → la mappa si ricentra
  // attorno all'utente senza bloccare l'apertura. Marina 2026-07-14.
  const aroundDoneRef = useRef(false);
  useEffect(() => {
    if (searchParams.get('around') !== '1' || aroundDoneRef.current) return;
    aroundDoneRef.current = true;
    const clearAround = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('around');
      setSearchParams(next, { replace: true });
    };
    if (!('geolocation' in navigator)) { clearAround(); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const d = 0.05; // ~5-6 km attorno all'utente
        const next = new URLSearchParams(searchParams);
        next.delete('around');
        next.set('center_lat', String(lat));
        next.set('center_lng', String(lng));
        next.set('center_label', 'Intorno a te');
        next.set('vp', `${lat - d},${lng - d * 1.4},${lat + d},${lng + d * 1.4}`);
        setSearchParams(next, { replace: true });
      },
      () => clearAround(),   // permesso negato → resta mappa "normale"
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('around')]);

  // Applica la zona/area passata via URL (ricerca dalla Home): area disegnata
  // (poligono) o center_lat/lng. Ri-applica quando la zona nell'URL CAMBIA (nuova
  // ricerca), leggendo sempre l'URL live — così anche se al primo render l'URL non
  // fosse ancora popolato, appena arriva il param la mappa si centra. Marina 2026-07-12.
  const urlZoneKeyRef = useRef(null);
  useEffect(() => {
    if (!isProspect || !mapReady || !mapRef.current || !googleRef.current) return;
    const sp = new URLSearchParams(window.location.search);
    const areaStr = sp.get('area');
    const cLat = parseFloat(sp.get('center_lat'));
    const cLng = parseFloat(sp.get('center_lng'));
    const key = areaStr ? ('a:' + areaStr)
      : (Number.isFinite(cLat) && Number.isFinite(cLng) ? `c:${cLat},${cLng}` : null);
    if (!key || urlZoneKeyRef.current === key) return; // niente zona o già applicata
    urlZoneKeyRef.current = key;
    const g = googleRef.current, map = mapRef.current;
    if (areaStr) {
      const path = areaStr.split(';').map((pair) => {
        const [la, lo] = pair.split(',').map(Number);
        return { lat: la, lng: lo };
      }).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      if (path.length >= 3) { commitDrawnArea(path); return; }
    }
    const d = 0.09;
    const box = { w: cLng - d, s: cLat - d, e: cLng + d, n: cLat + d };
    map.fitBounds(new g.maps.LatLngBounds({ lat: box.s, lng: box.w }, { lat: box.n, lng: box.e }), 20);
    loadedBoundsRef.current = box;
    loadedZoomRef.current = map.getZoom ? map.getZoom() : 13;
    setBbox(`${box.w.toFixed(5)},${box.s.toFixed(5)},${box.e.toFixed(5)},${box.n.toFixed(5)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspect, mapReady, searchParams.get('area'), searchParams.get('center_lat'), searchParams.get('center_lng')]);

  // OverlayView solo per la proiezione pixel→latlng (disegno freehand).
  useEffect(() => {
    if (!mapReady || !mapRef.current || !googleRef.current || projOverlayRef.current) return;
    const g = googleRef.current;
    const ov = new g.maps.OverlayView();
    ov.onAdd = () => {}; ov.draw = () => {}; ov.onRemove = () => {};
    ov.setMap(mapRef.current);
    projOverlayRef.current = ov;
  }, [mapReady]);

  // Disegno freehand dell'area: durante drawMode blocca il pan e traccia il tratto.
  useEffect(() => {
    if (!drawMode || !mapReady || !mapRef.current || !containerRef.current) return;
    const g = googleRef.current, map = mapRef.current, el = containerRef.current;
    const ov = projOverlayRef.current;
    const prev = { gh: map.get('gestureHandling'), drag: map.get('draggable') };
    map.setOptions({ gestureHandling: 'none', draggable: false, disableDoubleClickZoom: true });
    el.style.cursor = 'crosshair';

    let drawing = false, pts = [], line = null;
    const toLatLng = (cx, cy) => {
      const proj = ov && ov.getProjection && ov.getProjection();
      if (!proj) return null;
      const r = el.getBoundingClientRect();
      const ll = proj.fromContainerPixelToLatLng(new g.maps.Point(cx - r.left, cy - r.top));
      return ll ? { lat: ll.lat(), lng: ll.lng() } : null;
    };
    const start = (x, y) => {
      const ll = toLatLng(x, y); if (!ll) return;
      drawing = true; pts = [ll];
      line = new g.maps.Polyline({ map, path: [ll], strokeColor: '#1F6FEB', strokeWeight: 3, strokeOpacity: 0.95, zIndex: 6 });
    };
    const move = (x, y) => {
      if (!drawing) return;
      const ll = toLatLng(x, y); if (!ll) return;
      pts.push(ll);
      if (line) line.getPath().push(new g.maps.LatLng(ll.lat, ll.lng));
    };
    const end = () => {
      if (!drawing) return;
      drawing = false;
      if (line) { line.setMap(null); line = null; }
      if (pts.length >= 3) commitDrawnArea(pts);
      setDrawMode(false);
    };
    const onDown = (e) => { const t = e.touches ? e.touches[0] : e; e.preventDefault(); start(t.clientX, t.clientY); };
    const onMove = (e) => { if (!drawing) return; e.preventDefault(); const t = e.touches ? e.touches[0] : e; move(t.clientX, t.clientY); };
    const onUp = () => end();

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('touchstart', onDown, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('touchstart', onDown);
      el.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      el.style.cursor = '';
      if (line) line.setMap(null);
      map.setOptions({ gestureHandling: prev.gh || 'greedy', draggable: prev.drag !== false, disableDoubleClickZoom: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode, mapReady]);

  // Fingerprint stabile della LISTA di immobili (id+lat+lng): le patch in-place
  // su job_status / preferito / scartato / verdetto / url_report non cambiano il
  // fingerprint, quindi l'effetto marker non si ritriggera (e la mappa non dezooma
  // dopo l'avvio di un'analisi). Gli update visivi sui marker patchati sono già
  // fatti dentro wireUpPopupActions con setIcon/setContent.
  const rowsFingerprint = useMemo(
    () => rows.map((r) => `${r.id}|${r.lat}|${r.lng}`).join(';'),
    [rows]
  );

  // Render markers quando il SET di immobili o la mappa cambiano
  useEffect(() => {
    if (!mapReady || !mapRef.current || !googleRef.current) return;
    const google = googleRef.current;
    const map = mapRef.current;

    // DIFF-based: riusa marker già montati, calcola toAdd / toRemove e
    // riconcilia il clusterer SENZA distruggerlo. Quando cambia solo il
    // colore di un pin (es. reveal verdetto), il clusterer resta vivo →
    // nessuna sparizione momentanea del cluster icon.
    const __existingMarkers = new Map();
    markersRef.current.forEach((m) => {
      if (m._immobileId != null) __existingMarkers.set(m._immobileId, m);
    });
    const __addedMarkers = [];
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    let focusMarker = null;
    let hasMarkers = false;

    // Prospect: prima del reveal mostra solo il primary_interest; dopo il reveal, tutti.
    const prospectPrimaryId = user?.primary_interest?.immobile_id ?? null;
    rows.forEach((r, rowIdx) => {
      if (r.lat == null || r.lng == null) return;
      const isProspectPrimary = isProspect && prospectPrimaryId != null && r.id === prospectPrimaryId;
      // [TEST] Tutti i marker prospect appaiono insieme a fine zoom-in,
      // non più gating su markersRevealed (=apertura card) per i non-primary.
      // ⚠ Marina 2026-07-11: il gating su prospectZoomDone vale SOLO se c'è un
      // primary_interest (che fa partire l'intro). Un utente B2C entrato via login
      // WhatsApp NON ha primary_interest → l'intro non parte, prospectZoomDone resta
      // false e la mappa restava VUOTA. Senza primary_interest mostriamo subito i pin.
      if (isProspect && prospectPrimaryId != null && !prospectZoomDone) return;
      // Filtro per verdetto attivo (prospect): per i verdetti REALI (eccezionale/
      // consigliata/con_riserva/da_evitare) mostra solo i POI già analizzati DAL
      // prospect — il verdetto è noto solo dopo l'analisi, non va svelato col filtro.
      // I filtri di STATO ('analizzate','da_analizzare','in_analisi') NON vanno gatati:
      // non rivelano l'esito, servono a vedere lo stato dei pin (grigi finché non
      // aperti). Marina 2026-09-03: "Già analizzati" deve mostrare TUTTI gli immobili
      // con analisi di sistema, non solo quelli rivelati dal singolo utente.
      const realVerdettiActive = verdettoSet.has('eccezionale') || verdettoSet.has('consigliata')
        || verdettoSet.has('con_riserva') || verdettoSet.has('da_evitare');
      if (isProspect && realVerdettiActive && !prospectAnalyzedIds.has(r.id)) return;
      // Area disegnata attiva: mostra solo gli immobili DENTRO il poligono.
      if (isProspect && drawnPathRef.current && !pointInPolygon({ lat: r.lat, lng: r.lng }, drawnPathRef.current)) return;
      const pos = { lat: r.lat, lng: r.lng };
      // Prospect: "non_analizzato" finché non avvia l'analisi; se ha un job attivo
      // (queued/running) mostra "in_analisi" (spinner). Marina 2026-07-12.
      const realCategory = categoryFor(r);
      const jobActive = r.job_status === 'running' || r.job_status === 'queued';
      // "Nuovo" = uscito dopo l'ultima visita della ricerca (azzurro), se non analizzato.
      const isNewImm = isProspect && sinceTs && r.created_at && Date.parse(r.created_at) > sinceTs;
      const category = isProspect && !prospectAnalyzedIds.has(r.id)
        ? (jobActive ? 'in_analisi' : (isNewImm ? 'nuovo' : 'non_analizzato'))
        : realCategory;
      // Demo: pin più scuro per gli immobili con verdetto GIÀ pronto (reveal live),
      // senza svelare QUALE verdetto. Solo con ?demo=1.
      const demoReady = isProspect && demoMode && !!r.rivelasta_verdetto && !prospectAnalyzedIds.has(r.id);
      const color = demoReady
        ? '#20242B'
        : isProspect
          ? (PROSPECT_VERDETTO_COLORS[category] ?? PROSPECT_VERDETTO_COLORS.null)
          : markerColor(category, r.preferito);
      const priceLabel = formatPriceLabel((r.offerta_minima != null && Number(r.offerta_minima) > 0) ? r.offerta_minima : r.prezzo_base);
      // Content prospect: spinner se in analisi; altrimenti fumetto col prezzo
      // (o pallino se il prezzo manca), colorato per categoria/verdetto.
      const makeProspectContent = () => category === 'in_analisi'
        ? buildProspectAnalyzingContent(color)
        : iconToContent(priceLabel ? buildProspectPriceMarker(google, color, priceLabel) : buildProspectMarkerIcon(google, color));
      // Animazione di entrata:
      // - primary marker: bounce SMIL embedded nell'SVG (scale .4 → 1.45 → 1, KF.marker del design)
      // - altri prospect markers (dopo close card): DROP nativa
      const useDrop = isProspect && markersRevealed && prospectPrimaryId != null && r.id !== prospectPrimaryId;
      const usePrimaryBounce = isProspectPrimary && prospectZoomDone;

      // Riusa marker già esistente se è lo stesso id → niente flicker quando
      // markersRevealed flippa e si aggiungono solo gli altri pin.
      const existing = __existingMarkers.get(r.id);
      let marker;
      if (existing) {
        marker = existing;
        __existingMarkers.delete(r.id); // mark as kept
        // Se la categoria/colore è cambiata (es. analisi appena avviata → in_analisi,
        // o completata → verdetto), aggiorna il content del marker.
        if (existing._prospectColor !== color || existing._category !== category) {
          existing.content = isProspect
            ? makeProspectContent()
            : iconToContent(buildMarkerIcon(google, color, !!r.preferito, priceLabel));
        }
      } else {
        marker = advMarker(google, {
          position: pos,
          icon: isProspect
            ? buildProspectMarkerIcon(google, color)
            : buildMarkerIcon(google, color, !!r.preferito, priceLabel),
          title: `${r.comune || ''} — ${r.indirizzo || ''}`,
        });
        if (isProspect) marker.content = makeProspectContent();
        __addedMarkers.push(marker);
      }
      marker._priceLabel = priceLabel;
      marker._category = category;
      marker._preferito = !!r.preferito;
      marker._immobileId = r.id;
      marker._prospectColor = color;

      marker.addListener('gmp-click', () => {
        if (isProspect) {
          setHint((h) => h === 'ottimi' ? null : h);
          prospectFocalLatLngRef.current = null; // no cluster: halo segue marker
          setCarousel([r]);
          centerProspectMarkerBetweenChrome(r);
          return;
        }
        infoWindowRef.current.setContent(buildPopupHtml(r));
        infoWindowRef.current.open({ anchor: marker, map });
        setPopupOpen(true);
        google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
          wireUpPopupActions(r, marker, pos);
        });
      });

      markersRef.current.push(marker);
      bounds.extend(pos);
      hasMarkers = true;
      if (focusId && String(r.id) === String(focusId)) {
        focusMarker = marker;
      }
    });

    // Marker non più desiderati: rimasti in __existingMarkers perché non consumati.
    const __removedMarkers = Array.from(__existingMarkers.values());
    // Aggiorna rowsRef per il closure onClusterClick (che restiamo a usare lo stesso
    // riferimento del clusterer creato la prima volta).
    rowsRefForCluster.current = rows;

    if (!clustererRef.current && markersRef.current.length > 0) {
      // PRIMA volta: crea il clusterer
      const clusterOpts = {
        map,
        markers: markersRef.current,
        renderer: isProspect ? prospectClusterRenderer : clusterRenderer,
        algorithm: new SuperClusterAlgorithm({
          radius: 80,   // Marina 2026-07-11: cluster più aggressivo → meno pin a schermo
          maxZoom: 22,
          minPoints: 2,
        }),
      };
      if (isProspect) {
        clusterOpts.onClusterClick = (_event, cluster) => {
          // Marina 2026-07-11: cluster GRANDE (>5 elementi) → zoom-in per spezzarlo,
          // NON aprire lo slider. Solo i cluster piccoli (≤5) aprono il carosello.
          const markers = cluster.markers || [];
          const nMarkers = markers.length;
          const mp = mapRef.current;
          // ⚠ MA se i marker sono tutti (quasi) alle STESSE coordinate, nessun
          // livello di zoom li separerebbe → NON zoomare: mostra la lista.
          const EPS = 1e-5;
          let allCoincident = false;
          if (nMarkers >= 2) {
            const f = mLL(markers[0]);
            allCoincident = !!f && markers.every((m) => {
              const p = mLL(m);
              return p && Math.abs(p.lat - f.lat) < EPS && Math.abs(p.lng - f.lng) < EPS;
            });
          }
          if (nMarkers > 5 && !allCoincident && mp) {
            const p = cluster.position;
            if (p) mp.setCenter({ lat: p.lat(), lng: p.lng() });
            mp.setZoom(Math.min(20, (mp.getZoom() || 10) + 2));
            return;
          }
          const ids = (cluster.markers || []).map((m) => m._immobileId).filter(Boolean);
          const curRows = rowsRefForCluster.current || [];
          const idToRow = new Map(curRows.map((r) => [r.id, r]));
          const carouselRows = ids.map((id) => idToRow.get(id)).filter(Boolean);
          if (carouselRows.length) {
            const pos = cluster.position;
            const focal = pos ? { lat: pos.lat(), lng: pos.lng() } : carouselRows[0];
            prospectFocalLatLngRef.current = focal;
            setCarousel(carouselRows);
            setHint((h) => h === 'ottimi' ? null : h);
            centerProspectMarkerBetweenChrome(focal);
          }
        };
      } else {
        // B2B: se il cluster contiene marker a coordinate (quasi) coincidenti,
        // lo zoom-in default non li separa mai — regressione da maxZoom 16→22
        // (commit 0e64d05). Mostriamo un InfoWindow con la lista degli annunci
        // del cluster; click su un item apre il popup del marker corrispondente.
        // Se le coord SONO distinte lasciamo il default (zoom-in su cluster).
        clusterOpts.onClusterClick = (event, cluster, mapInstance) => {
          const markers = cluster.markers || [];
          if (markers.length < 2) return; // niente da disambiguare
          // Delta max ~ 1.1 metri: se tutti i marker cadono in questo raggio,
          // lo zoom non aiuta e vogliamo lista.
          const EPS = 1e-5;
          const first = mLL(markers[0]);
          const allCoincident = markers.every((m) => {
            const p = mLL(m);
            return Math.abs(p.lat - first.lat) < EPS
                && Math.abs(p.lng - first.lng) < EPS;
          });
          if (!allCoincident) return; // default MarkerClusterer: zoom-in
          const curRows = rowsRefForCluster.current || [];
          const idToRow = new Map(curRows.map((r) => [r.id, r]));
          const items = markers.map((m) => ({ marker: m, row: idToRow.get(m._immobileId) })).filter((x) => x.row);
          if (!items.length) return;
          const html = '<div class="mp-cluster-list" style="max-height:260px;overflow:auto">'
            + '<div style="font-weight:600;font-size:12px;color:#555;margin-bottom:6px">'
            + items.length + ' annunci in questa posizione</div>'
            + items.map((it, i) => {
                const r = it.row;
                const titolo = (r.tipologia || r.categoria_catastale_vocab || 'Immobile');
                const via = (r.indirizzo || '') + (r.comune ? (r.indirizzo ? ', ' : '') + r.comune : '');
                const prezzo = r.prezzo_base ? '€ ' + Number(r.prezzo_base).toLocaleString('it-IT') : '';
                return '<a href="#" data-mp-idx="' + i + '" '
                     + 'style="display:block;padding:6px 4px;border-top:1px solid #eee;text-decoration:none;color:#111">'
                     + '<div style="font-weight:600;font-size:13px">' + titolo + '</div>'
                     + '<div style="font-size:12px;color:#555">' + via + '</div>'
                     + (prezzo ? '<div style="font-size:12px;color:#0a7">' + prezzo + '</div>' : '')
                     + '</a>';
              }).join('')
            + '</div>';
          infoWindowRef.current.setContent(html);
          infoWindowRef.current.setPosition(first);
          infoWindowRef.current.open(mapInstance || map);
          setPopupOpen(true);
          google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
            document.querySelectorAll('.mp-cluster-list a[data-mp-idx]').forEach((a) => {
              a.addEventListener('click', (ev) => {
                ev.preventDefault();
                const idx = Number(a.getAttribute('data-mp-idx'));
                const it = items[idx];
                if (!it) return;
                infoWindowRef.current.close();
                setPopupOpen(false);
                google.maps.event.trigger(it.marker, 'click');
              });
            });
          });
        };
      }
      clustererRef.current = new MarkerClusterer(clusterOpts);
      if (isProspect && clustererRef.current.addListener) {
        clustererRef.current.addListener('clusteringend', () => {
          applyActiveClusterColorRef.current();
        });
      }
      // I marker rimasti che non sono stati inseriti nel clusterer: rimuovili dalla mappa
      __removedMarkers.forEach((m) => { try { m.map = null; } catch (_) {} });
    } else if (clustererRef.current) {
      // SUCCESSIVE: riconcilia in-place — nessun teardown del clusterer
      if (__removedMarkers.length) {
        try { clustererRef.current.removeMarkers(__removedMarkers, true); } catch (_) {}
        __removedMarkers.forEach((m) => { try { m.map = null; } catch (_) {} });
      }
      if (__addedMarkers.length) {
        try { clustererRef.current.addMarkers(__addedMarkers, true); } catch (_) {}
      }
      // Forza un re-render del clusterer dopo le modifiche (noDraw=true negli add/remove)
      try { clustererRef.current.render(); } catch (_) {}
    } else {
      // No clusterer e nessun marker: pulisci eventuali residui
      __removedMarkers.forEach((m) => { try { m.map = null; } catch (_) {} });
    }

    if (focusMarker && !focusDoneRef.current) {
      // UNA sola volta: centra, zooma e apri la card. Dopo, l'utente naviga libero.
      focusDoneRef.current = true;
      map.setCenter(mLL(focusMarker));
      map.setZoom(17); // sopra maxZoom del clusterer → marker singolo
      google.maps.event.trigger(focusMarker, 'gmp-click');
    } else if (isProspect && searchParams.get('view') === 'preferiti' && hasMarkers && shouldFitRef.current) {
      // Vista Preferiti: non sappiamo dove siano → fitta la mappa sull'area più
      // piccola che contiene TUTTI i preferiti (come da Analizzati→Preferiti).
      // Marina 2026-07-14. bottom ampio per la card/chrome B2C.
      shouldFitRef.current = false;
      map.fitBounds(bounds, { top: 80, right: 40, bottom: 200, left: 40 });
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
      });
    } else if (isProspect && verdettoSet.size > 0 && hasMarkers && shouldFitRef.current) {
      // Filtro verdetto in B2C: ri-fitta la mappa su TUTTI i risultati con quel
      // verdetto (come B2B), così li vedi anche se erano fuori dalla vista corrente.
      // Marina 2026-07-11. bottom ampio per lasciare spazio alla card/chrome B2C.
      shouldFitRef.current = false;
      map.fitBounds(bounds, { top: 80, right: 40, bottom: 200, left: 40 });
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
      });
    } else if (isProspect) {
      // Prospect: centra sull'immobile di interesse SOLO al primo caricamento.
      // I cambi di filtro (verdetto, ecc.) NON devono resettare lo zoom/centro.
      if (!prospectInitialCenteredRef.current) {
        const pi = user?.primary_interest;
        if (pi && pi.lat && pi.lng) {
          map.setCenter({ lat: pi.lat, lng: pi.lng });
          prospectInitialCenteredRef.current = true;
          // Zoom-in animato: parte da 6 (Italia), arriva a 13. Step ~160ms = ~1.1s totali.
          // A fine animazione setProspectZoomDone(true) → primary marker entra con bounce.
          if (!prospectZoomTriggeredRef.current) {
            prospectZoomTriggeredRef.current = true;
            map.setZoom(6);
            let z = 6;
            const TARGET = 13;
            setTimeout(() => {
              const iv = setInterval(() => {
                z += 1;
                const isLast = z >= TARGET;
                if (isLast) {
                  clearInterval(iv);
                  // Ultimo step: aspetto 'idle' (camera settled), poi ~900ms
                  // di respiro prima di mostrare i POI → l'occhio percepisce
                  // separatamente la fine zoom e l'apparizione dei pin.
                  google.maps.event.addListenerOnce(map, 'idle', () => {
                    setTimeout(() => setProspectZoomDone(true), 900);
                  });
                }
                map.setZoom(z);
              }, 220); // [TEST] rallentato da 150 → 220ms per step
            }, 280);
          }
        }
      }
    } else if (hasMarkers && shouldFitRef.current) {
      // fitBounds SOLO su primo load / cambio filtro (shouldFitRef). Sui refetch
      // da pan/zoom (viewport) NON si rifitta → niente loop camera→idle→refetch.
      shouldFitRef.current = false;
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      // Cap massimo zoom dopo fitBounds
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 14) map.setZoom(14);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsFingerprint, mapReady, focusId, prospectAnalyzedIds, prospectZoomDone, user?.primary_interest?.immobile_id, drawnArea, sinceTs]);

  /** Parse codice catastale da `tipologia` text */
  function catCatastale(t) {
    if (!t) return null;
    const s = t.toLowerCase();
    if (/sign/.test(s))        return 'A/1';
    if (/tipo civ/.test(s))    return 'A/2';
    if (/tipo eco/.test(s))    return 'A/3';
    if (/tipo pop/.test(s))    return 'A/4';
    if (/villin/.test(s))      return 'A/7';
    if (/ville/.test(s))       return 'A/8';
    if (/uffic/.test(s))       return 'A/10';
    if (/magazz/.test(s))      return 'C/2';
    if (/appartament/.test(s)) return 'Appart.';
    return null;
  }

  function buildPopupHtml(r) {
    // Popup ridotto per prospect: solo verdetto + leggi report + preferito
    if (isProspect) {
      return buildProspectPopupHtml(r);
    }
    const verd = r.rivelasta_verdetto && VERDETTO_COLORS[r.rivelasta_verdetto];
    const cat = catCatastale(r.tipologia);
    const locali = [
      r.n_camere ? `${r.n_camere} cam` : null,
      r.n_bagni ? `${r.n_bagni} bagni` : null,
    ].filter(Boolean).join(', ');
    const gap = r.gap_percentuale != null ? Number(r.gap_percentuale) : null;
    const gapColor = gap == null ? '#404040'
      : (gap < 0 ? '#16a34a' : (gap > 15 ? '#dc2626' : '#d97706'));
    const gapText = gap == null ? '—' : (gap > 0 ? '+' : '') + gap.toFixed(1) + '%';
    const tier = r.liquidita_tier ?? null;
    const tierLbl = ({2:'Alta',3:'Media',4:'Bassa'})[tier] || (tier ? 'T'+tier : '—');

    const row = (label, value, valStyle = '') =>
      `<div style="display:flex;justify-content:space-between;gap:12px;margin:2px 0;color:#404040;font-size:12px;">
        <span style="color:#737373;font-size:10.5px;text-transform:uppercase;letter-spacing:0.04em;">${label}</span>
        <span style="font-weight:500;${valStyle}">${value}</span>
      </div>`;

    // Stile bottoni unificato — coerente con il resto dell'app. Tutti full-width
    // perché Google InfoWindow restringe la popup vicino ai bordi della mappa: una
    // griglia 2-col farebbe wrappare le label corte (es. "Street View") in modo brutto.
    const btnBase = 'display:flex;align-items:center;justify-content:center;gap:6px;'
      + 'padding:8px 12px;border:0;border-radius:8px;font:600 12.5px/1.2 Geist,system-ui,sans-serif;'
      + 'cursor:pointer;text-decoration:none;width:100%;box-sizing:border-box;'
      + 'white-space:nowrap;';
    const btnPrimary  = btnBase + 'background:oklch(0.45 0.18 250);color:#fff;';
    const btnSuccess  = btnBase + 'background:oklch(0.55 0.15 145);color:#fff;';
    const btnAccent   = btnBase + 'background:oklch(0.50 0.22 290);color:#fff;';
    const btnWarning  = btnBase + 'background:oklch(0.62 0.16 65);color:#fff;';
    const btnNeutral  = btnBase + 'background:#f4f4f5;color:#27272a;border:1px solid #e4e4e7;';
    const btnDark     = btnBase + 'background:#27272a;color:#fff;';

    // Azione astemaster: report già pronto / job in corso / avvia
    let mainAction = '';
    if (r.url_report) {
      const repeatStyle = r.needs_reanalysis ? btnAccent : btnNeutral;
      const repeatTitle = r.needs_reanalysis ? "PVP aggiornato dopo l'analisi — consigliato ripetere" : 'Rilancia l\'analisi con dati aggiornati';
      mainAction = `<a href="${r.url_report}" target="_blank" rel="noopener" style="${btnSuccess}">Apri report</a>`
        + ` <a href="/api/immobili/${r.id}/report.pdf" target="_blank" rel="noopener" style="${btnNeutral}" title="Scarica il report in PDF">PDF</a>`
        + (r.url_pvp ? ` <button data-action="repeat-astemaster" data-id="${r.id}" type="button" style="${repeatStyle}" title="${repeatTitle}">↻ Ripeti${r.needs_reanalysis ? ' ⚠' : ''}</button>` : '');
    } else if (r.job_status === 'running') {
      mainAction = `<button data-action="resume" data-id="${r.id}" type="button" style="${btnPrimary}">In elaborazione…</button>`;
    } else if (r.job_status === 'queued') {
      mainAction = `<button data-action="resume" data-id="${r.id}" type="button" style="${btnWarning}">In coda</button>`;
    } else if (r.url_pvp) {
      mainAction = `<button data-action="start-astemaster" data-id="${r.id}" type="button" style="${btnAccent}">Avvia analisi</button>`;
    }

    const prefStyle = r.preferito ? btnWarning : btnNeutral;
    const prefLbl = r.preferito ? 'Tolto pref.' : 'Preferito';

    const scartStyle = r.scartato ? btnSuccess : btnNeutral;
    const scartLbl = r.scartato ? 'Rivaluta' : 'Scarta';

    return `
      <div style="font-family:Geist,system-ui,sans-serif;width:260px;max-width:100%;box-sizing:border-box;">
        <div style="font-weight:600;font-size:14px;line-height:1.3;margin-bottom:2px;color:#0a0a0a;word-break:break-word;">${r.indirizzo || '—'}</div>
        <div style="color:#64748b;font-size:11.5px;margin-bottom:10px;word-break:break-word;">${r.comune || ''}${r.tribunale ? ' · ' + r.tribunale : ''}</div>

        ${tier ? row('Liquidità', tierLbl, 'font-weight:700') : ''}
        ${cat ? row('Cat. catastale', cat) : ''}
        ${r.piano ? row('Piano', r.piano) : ''}
        ${row('Superficie', (r.superficie_mq ? r.superficie_mq + ' mq' : '—') + (locali ? ' · ' + locali : ''))}
        ${r.disponibilita ? row('Disponibilità', r.disponibilita) : ''}
        ${row('Offerta min.', r.offerta_minima != null ? fmtEuro0(r.offerta_minima) : '—', 'font-weight:600')}
        ${r.prezzo_base != null ? row('Prezzo base', fmtEuro0(r.prezzo_base)) : ''}
        ${row('Gap %', gapText, `color:${gapColor};font-weight:600`)}
        ${verd ? row('Verdetto', r.rivelasta_verdetto.replace(/_/g,' '), `color:${verd};font-weight:700;text-transform:capitalize`) : ''}
        ${row('Data asta', fmtDate(r.data_vendita))}

        <div style="display:flex;flex-direction:column;gap:6px;margin-top:12px;">
          ${mainAction}
          ${r.url_pvp ? `<a href="${r.url_pvp}" target="_blank" rel="noopener" style="${btnDark}">Apri su PVP ↗</a>` : ''}
          <button data-action="streetview" data-id="${r.id}" type="button" style="${btnDark}">Street View</button>
          ${r.omi_report_url ? `<a href="${r.omi_report_url}" target="_blank" rel="noopener" style="${btnSuccess}" title="Apri il report OMI già generato">📊 Apri analisi OMI</a>` : ''}
          ${r.indirizzo && r.comune ? `<button data-action="omi-report" data-id="${r.id}" type="button" style="${btnNeutral}" title="Compravendite dichiarate all'AdE nella zona">📊 ${r.omi_report_url ? 'Rigenera' : 'Analisi Deep'} OMI</button>` : ''}
          <div style="display:flex;gap:6px;">
            <button data-action="toggle-pref" data-id="${r.id}" type="button" style="${prefStyle}">${prefLbl}</button>
            <button data-action="toggle-scart" data-id="${r.id}" type="button" style="${scartStyle}">${scartLbl}</button>
          </div>
        </div>
      </div>
    `;
  }

  /** Popup minimale per prospect: verdetto + leggi analisi + preferiti */
  function buildProspectPopupHtml(r) {
    const verd = r.rivelasta_verdetto && VERDETTO_COLORS[r.rivelasta_verdetto];
    const verdLabel = r.rivelasta_verdetto ? r.rivelasta_verdetto.replace(/_/g, ' ') : 'Non analizzato';
    const btnBase = 'display:flex;align-items:center;justify-content:center;gap:6px;'
      + 'padding:9px 12px;border:0;border-radius:8px;font:600 13px/1.2 Geist,system-ui,sans-serif;'
      + 'cursor:pointer;text-decoration:none;width:100%;box-sizing:border-box;';
    const btnPrimary = btnBase + 'background:oklch(0.45 0.18 250);color:#fff;';
    const btnNeutral = btnBase + 'background:#f4f4f5;color:#27272a;border:1px solid #e4e4e7;';
    const btnWarning = btnBase + 'background:oklch(0.62 0.16 65);color:#fff;';
    const prefStyle = r.preferito ? btnWarning : btnNeutral;
    const prefLbl   = r.preferito ? '★ Tolto preferito' : '☆ Aggiungi ai preferiti';

    return `
      <div style="font-family:Geist,system-ui,sans-serif;width:240px;max-width:100%;box-sizing:border-box;">
        <div style="font-weight:600;font-size:14px;line-height:1.3;margin-bottom:2px;color:#0a0a0a;word-break:break-word;">${r.indirizzo || '—'}</div>
        <div style="color:#64748b;font-size:11.5px;margin-bottom:12px;">${r.comune || ''}</div>
        ${verd
          ? `<div style="display:inline-block;padding:4px 10px;border-radius:999px;background:${verd};color:#fff;font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:12px;">${verdLabel}</div>`
          : `<div style="font-size:12px;color:#737373;margin-bottom:12px;">Analisi non ancora disponibile</div>`}
        <div style="display:flex;flex-direction:column;gap:6px;">
          ${r.url_report
            ? `<a href="${r.url_report}" target="_blank" rel="noopener" style="${btnPrimary}">Leggi il verdetto</a>`
            : `<button type="button" disabled style="${btnNeutral};opacity:0.55;cursor:not-allowed;">Verdetto non disponibile</button>`}
          ${r.url_report
            ? `<button data-action="condividi" data-id="${r.id}" type="button" style="${btnNeutral}">Condividi</button>`
            : ''}
          <button data-action="toggle-pref" data-id="${r.id}" type="button" style="${prefStyle}">${prefLbl}</button>
        </div>
      </div>
    `;
  }

  /** Condivisione dalla card del prospect: usa l'unica implementazione condivisa. */
  async function condividiReportProspect(row, btn) {
    await condividiReportCondiviso(row, btn);
  }

  /**
   * Condivide il report: share sheet nativo dove c'è, copia negli appunti dove no.
   *
   * Il link è `/r/<token>` e si apre senza login (Michael, 16/09/2026). NON è il
   * percorso del file: `/reports/analisi_<pvp>.html` si indovina da un numero PVP.
   *
   * `navigator.share` esiste su iOS e Android e apre il foglio di sistema — è lì
   * che questi link si mandano, su WhatsApp. Sul desktop quasi mai, e allora si
   * copia l'indirizzo e si dice che è stato copiato: un tasto che non dà risposta
   * sembra rotto.
   */
  async function condividiReport(r, btn) {
    const testoOriginale = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Preparo il link…';

    try {
      const { url, titolo } = await creaLinkCondivisione(r.id);

      if (navigator.share) {
        try {
          await navigator.share({
            title: titolo || 'Analisi immobile',
            text: `Analisi di ${titolo || 'questo immobile'} — RivelAsta`,
            url,
          });
          btn.textContent = 'Condiviso ✓';
        } catch (e) {
          // L'utente ha chiuso il foglio di condivisione: non è un errore, e non
          // va segnalato come tale.
          if (e?.name === 'AbortError') {
            btn.textContent = testoOriginale;
            btn.disabled = false;
            return;
          }
          throw e;
        }
      } else {
        await copiaNegliAppunti(url);
        btn.textContent = 'Link copiato ✓';
        mostraToast('Link copiato: chi lo apre vede il report senza registrarsi.');
      }
    } catch (e) {
      const msg = e?.response?.data?.error || 'Non è stato possibile creare il link.';
      btn.textContent = 'Non riuscito';
      mostraToast(msg);
      console.error('[condividi]', e);
    }

    setTimeout(() => {
      btn.textContent = testoOriginale;
      btn.disabled = false;
    }, 2500);
  }

  /** Copia negli appunti, con la via vecchia dove l'API non c'è (http, Safari datati). */
  async function copiaNegliAppunti(testo) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(testo);
        return;
      } catch (e) {
        // permesso negato o pagina non sicura: si ripiega sotto
      }
    }
    const ta = document.createElement('textarea');
    ta.value = testo;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  /** Avviso breve in fondo allo schermo: sul telefono è dove si guarda. */
  function mostraToast(testo) {
    const vecchio = document.getElementById('rivelasta-toast');
    if (vecchio) vecchio.remove();

    const el = document.createElement('div');
    el.id = 'rivelasta-toast';
    el.textContent = testo;
    el.style.cssText = [
      'position:fixed', 'left:50%', 'transform:translateX(-50%)',
      'bottom:calc(24px + env(safe-area-inset-bottom))',
      'max-width:min(420px, calc(100vw - 32px))',
      'padding:12px 18px', 'border-radius:10px',
      'background:#0a0a0a', 'color:#fff', 'font-size:13.5px', 'line-height:1.45',
      'text-align:center', 'z-index:2147483647',
      'box-shadow:0 8px 24px rgba(0,0,0,0.25)',
      'font-family:Geist,system-ui,-apple-system,sans-serif',
    ].join(';');
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }

  /** Aggiorna il record locale + ridisegna il popup (senza riaprire) */
  function patchRow(id, patch) {
    setRows((rs) => rs.map((row) => row.id === id ? { ...row, ...patch } : row));
  }

  function wireUpPopupActions(r, marker, pos) {
    const google = googleRef.current;
    const map = mapRef.current;
    const container = document.querySelector('.gm-style-iw');
    if (!container) return;

    container.querySelectorAll('[data-action]').forEach((btn) => {
      const action = btn.getAttribute('data-action');
      btn.onclick = async (e) => {
        e.preventDefault();
        const id = Number(btn.getAttribute('data-id'));
        try {
          if (action === 'condividi') {
            await condividiReport(r, btn);
          } else if (action === 'streetview') {
            openStreetView(google, map, pos);
          } else if (action === 'omi-report') {
            // Chiudo l'InfoWindow prima di aprire la modale, altrimenti ne
            // avremmo 2 overlay attivi contemporaneamente.
            infoWindowRef.current?.close();
            setPopupOpen(false);
            setOmiRow(r);
          } else if (action === 'start-astemaster' || action === 'repeat-astemaster') {
            const isRepeat = action === 'repeat-astemaster';
            if (isRepeat) {
              const ok = window.confirm(`Ripetere l'analisi per questo immobile?\nIl report precedente verrà sovrascritto.`);
              if (!ok) return;
            }
            btn.disabled = true;
            btn.textContent = '🟡 Avvio…';
            const res = await startAstemaster(id, { force: isRepeat });
            const patch = { job_status: res.status || 'queued', ...(isRepeat ? { url_report: null, needs_reanalysis: false } : {}) };
            patchRow(id, patch);
            const newR = { ...r, ...patch };
            infoWindowRef.current.setContent(buildPopupHtml(newR));
            google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
              wireUpPopupActions(newR, marker, pos);
            });
            marker._category = 'in_analisi';
            marker.content = iconToContent(buildMarkerIcon(google, markerColor('in_analisi', r.preferito), r.preferito, marker._priceLabel));
            pollAstemaster(id, marker, pos);
          } else if (action === 'resume') {
            pollAstemaster(id, marker, pos);
            // Apre anche il job status come notifica leggera
            const s = await astemasterStatus(id);
            window.alert(`Stato analisi:\n${s.status}${s.queue_position != null ? ` (posizione coda: ${s.queue_position})` : ''}${s.elapsed_seconds ? `\nElapsed: ${s.elapsed_seconds}s` : ''}`);
          } else if (action === 'toggle-pref') {
            const res = await apiTogglePreferito(id);
            patchRow(id, { preferito: res.preferito });
            const newR = { ...r, preferito: res.preferito };
            infoWindowRef.current.setContent(buildPopupHtml(newR));
            google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
              wireUpPopupActions(newR, marker, pos);
            });
            // Aggiorna anche l'icona del marker (anello arancione)
            marker.content = iconToContent(buildMarkerIcon(google, markerColor(categoryFor(r), res.preferito), res.preferito, marker._priceLabel));
          } else if (action === 'toggle-scart') {
            const res = await apiToggleScarta(id);
            patchRow(id, { scartato: res.scartato });
            // Rimuovi dal mappa se siamo in view valutazione e diventa scartato
            const currentView = (new URLSearchParams(window.location.search)).get('view') || 'valutazione';
            if (currentView === 'valutazione' && res.scartato) {
              marker.map = null;
              infoWindowRef.current.close();
            } else {
              const newR = { ...r, scartato: res.scartato };
              infoWindowRef.current.setContent(buildPopupHtml(newR));
              google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                wireUpPopupActions(newR, marker, pos);
              });
            }
          }
        } catch (err) {
          window.alert('Errore: ' + (err?.response?.data?.error || err?.message || 'unknown'));
          btn.disabled = false;
        }
      };
    });
  }

  /** Polling stato job astemaster — aggiorna popup quando completato */
  async function pollAstemaster(id, marker, pos) {
    const tick = async () => {
      try {
        const s = await astemasterStatus(id);
        if (s.status === 'done' && s.url_report) {
          patchRow(id, { job_status: null, url_report: s.url_report, rivelasta_verdetto: s.rivelasta_verdetto });
          // Trova la row aggiornata e re-render popup
          setRows((rs) => {
            const row = rs.find((x) => x.id === id);
            if (row && infoWindowRef.current) {
              const newR = { ...row, job_status: null, url_report: s.url_report, rivelasta_verdetto: s.rivelasta_verdetto };
              infoWindowRef.current.setContent(buildPopupHtml(newR));
              googleRef.current?.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                wireUpPopupActions(newR, marker, pos);
              });
              // Aggiorna marker color (categoria deriva dal nuovo verdetto + niente job)
              marker._category = s.rivelasta_verdetto || 'non_analizzato';
              marker.content = iconToContent(buildMarkerIcon(googleRef.current, markerColor(marker._category, row.preferito), row.preferito, marker._priceLabel));
            }
            return rs;
          });
          return; // stop polling
        }
        if (s.status === 'failed') return; // stop polling
        setTimeout(tick, 5000);
      } catch {}
    };
    setTimeout(tick, 3000);
  }

  // Apri Street View sulla posizione del marker
  function openStreetView(google, map, position) {
    const sv = map.getStreetView();
    sv.setPosition(position);
    sv.setPov({ heading: 0, pitch: 0 });
    sv.setZoom(0);
    sv.setVisible(true);
  }

  const updateFilter = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  };

  const mainStyle = fullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        // dvh = dynamic viewport height: si adatta quando la URL bar del browser
        // mobile compare/scompare. Senza questo, su mobile la mappa va oltre il
        // visibile (legenda tagliata) finché non scrolli per nascondere la URL bar.
        height: '100dvh',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        // svh = small viewport height: assume sempre il caso peggiore (URL bar visibile)
        // così la mappa non esce mai dal visibile su mobile.
        // -53px solo se c'è la Topbar del layout (non per prospect, che non l'ha).
        height: isProspect ? '100svh' : 'calc(100svh - 53px)',
        // NIENTE overflow auto: la mappa deve riempire, non far scrollare la pagina
        overflow: 'hidden',
      };

  return (
    <main className={`content content--inbox${fullscreen ? ' content--fullscreen' : ''}${isProspect && !fullscreen ? ' content--prospect-map prospect-redesign prospect-redesign-page' : ''}`} style={mainStyle}>
      {/* {isProspect && <ProspectBoot />} -- temporaneamente disabilitato per debug white screen */}
      {!fullscreen && !isProspect && (
        <div className="page__header" style={{ flex: '0 0 auto' }}>
          <div className="page__title-wrap">
            <h1 className="page__title">Mappa immobili</h1>
            <p className="page__sub">
              {loading ? 'Caricamento…' : `${(totalCount || rows.length).toLocaleString('it-IT')} immobili georefernziati`}
            </p>
          </div>
          <div className="page__actions">
            <Link to="/app/immobili" className="btn btn--ghost" style={{ textDecoration: 'none' }}>
              <IList size={13} />
              <span>Vista lista</span>
            </Link>
          </div>
        </div>
      )}

      {!fullscreen && searchParams.get('only_matched') === '1' && (
        <div style={{
          padding: '8px 16px', background: 'oklch(0.96 0.04 250)',
          border: '1px solid oklch(0.82 0.10 250)', borderRadius: 8,
          margin: '0 16px 8px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          fontSize: 13, color: 'var(--fg)',
        }}>
          <span><strong>Filtrata sui tuoi match</strong> · solo immobili compatibili con i tuoi Lead</span>
          <Link to="/app/match" style={{
            marginLeft: 'auto', fontSize: 12, fontWeight: 500,
            color: 'oklch(0.45 0.18 250)', textDecoration: 'none',
          }}>← Torna a Match Inbox</Link>
          <button type="button" onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('only_matched');
            setSearchParams(next, { replace: true });
          }} style={{
            background: 'transparent', border: 0, cursor: 'pointer',
            color: 'var(--fg-muted)', fontSize: 12,
          }}>✕ rimuovi filtro</button>
        </div>
      )}

      {!fullscreen && !isProspect && (
        <div className="im-toolbar" style={{ flex: '0 0 auto' }}>
          <div className="im-toolbar__right" style={{ marginLeft: 'auto' }}>
            <div className="mi-search">
              <ISearch size={14} className="mi-search__icon" />
              <input
                type="text"
                className="mi-search__input"
                placeholder="Cerca comune, indirizzo, tribunale…"
                value={query}
                onChange={(e) => updateFilter({ q: e.target.value })}
              />
            </div>
            {verdettoSet.size > 0 && (
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => updateFilter({ verdetto: '' })}
                title="Rimuovi tutti i filtri verdetto"
                style={{ whiteSpace: 'nowrap' }}
              >
                {verdettoSet.size} verdett{verdettoSet.size === 1 ? 'o' : 'i'} ✕
              </button>
            )}
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => setFilterPanelOpen(true)}
              aria-haspopup="dialog"
            >
              <IFilter size={13} />
              <span>Filtri</span>
              {activeFilterCount > 0 && <span className="fp__btn-count">{activeFilterCount}</span>}
            </button>
          </div>
        </div>
      )}

      {/* Overlay modale per onboarding: oscura tutto */}
      {isProspect && hint === 'intro' && (
        <div style={modalOverlay} />
      )}
      {/* Per il secondo tooltip oscuro ma lascio i markers cliccabili (passthrough) */}
      {isProspect && hint === 'ottimi' && (
        <div style={modalOverlayPassthrough} />
      )}
      {/* Oscura mentre l'InfoWindow del marker è aperto (passthrough sull'InfoWindow) */}
      {isProspect && popupOpen && (
        <div style={modalOverlayPassthrough} />
      )}

      {/* Prospect TOPBAR redesign */}
      {isProspect && (() => {
        const zoneLabel = (searchParams.get('center_label') || user?.primary_interest?.label || '—')
          .split(',')[0].trim() || '—';

        return (
        <div className="pr-topbar">
          <div className="pr-topbar__row">
            <button className="pr-pill pr-iconbtn"
                    onClick={() => navigate('/app/home')}
                    aria-label="Torna alla home"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#3A3F45" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button className="pr-pill pr-radar-label" onClick={() => setZoneSheetOpen(true)}
                    style={{ textAlign: 'left', border: 0, cursor: 'pointer', display: 'flex',
                             flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start',
                             gap: 8, padding: '0 12px', overflow: 'hidden' }}
                    aria-label="Scegli la zona">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2" style={{ flex: '0 0 auto' }}>
                <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" /><circle cx="12" cy="10" r="2.4" />
              </svg>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                <div className="pr-radar-label__top">{zoneLabel !== '—' ? 'Zona attiva' : 'Scegli la zona'}</div>
                <div className="pr-radar-label__main" title={zoneLabel} style={{ padding: 0 }}>{zoneLabel !== '—' ? zoneLabel : 'Cerca o disegna'}</div>
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9EA4" strokeWidth="2" style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <button className="pr-pill pr-iconbtn"
                    onClick={() => setNotifyModalOpen(true)}
                    aria-label="Notifiche WhatsApp">
              <svg width="22" height="22" viewBox="0 0 24 24" fill={waNotifEnabled ? '#25D366' : '#9A9EA4'}>
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
              </svg>
            </button>
          </div>

          {/* <ProspectTicker /> nascosta temporaneamente */}

          <ProspectFilters
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            filterOptions={filterOptions}
            discoveredVerdetti={discoveredVerdetti}
            onVerdettoBlocked={() => setEmptyFilterModalOpen(true)}
          />
        </div>
        );
      })()}

      <div style={{ flex: '1 1 auto', position: 'relative', minHeight: 0 }}>
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        {/* L'halo prospect ora è renderizzato da ProspectHaloOverlayClass dentro
            il pane overlayLayer di Google (sotto i marker) — niente div qui */}

        {/* Fase 2 onboarding: tooltip ancorato sopra un marker specifico */}
        {isProspect && hint === 'ottimi' && markerHintPos && (
          <div style={{
            position: 'absolute',
            left: Math.max(8, Math.min(markerHintPos.x - 110, (containerRef.current?.clientWidth || 360) - 228)),
            top: Math.max(8, markerHintPos.y - 92),
            width: 220, maxWidth: 'calc(100vw - 28px)',
            background: 'oklch(0.25 0.04 250)', color: '#fff',
            padding: '10px 14px', borderRadius: 10,
            boxShadow: '0 8px 24px oklch(0 0 0 / 0.25)',
            zIndex: 61, pointerEvents: 'auto',
          }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>🟢 Tocca un pallino</div>
            <div style={{ fontSize: 12, lineHeight: 1.4 }}>
              Toccalo per vedere il verdetto.
            </div>
            <div style={{
              position: 'absolute',
              bottom: -8,
              left: Math.min(Math.max(markerHintPos.x - Math.max(8, Math.min(markerHintPos.x - 110, (containerRef.current?.clientWidth || 360) - 228)), 18), 220 - 18),
              width: 0, height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid oklch(0.25 0.04 250)',
            }} />
          </div>
        )}

        {/* Carousel cluster (prospect): alternativa allo zoom — mostra le card degli immobili */}
        {/* Tab bar mobile stile immobiliare.it (Marina 2026-07-11) — sostituisce il
            vecchio FAB "Attiva radar". Nascosta quando è aperta la card. */}
        {isProspect && !(carousel && carousel.length > 0) && (() => {
          const viewPref = searchParams.get('view') === 'preferiti';
          const activeTab = viewPref ? 'preferiti' : 'mappa';
          const setView = (val) => {
            // Passando a Preferiti: parti da tutta Italia (non sappiamo dove siano),
            // poi il fetch carica tutti i preferiti e la mappa fitta sull'area minima
            // che li contiene. Marina 2026-07-14.
            if (val === 'preferiti' && mapRef.current && googleRef.current) {
              const IT = { s: 36.4, n: 47.2, w: 6.5, e: 18.6 };
              try {
                mapRef.current.fitBounds(new googleRef.current.maps.LatLngBounds(
                  { lat: IT.s, lng: IT.w }, { lat: IT.n, lng: IT.e }));
              } catch (_) {}
            }
            const next = new URLSearchParams(searchParams);
            if (val) next.set('view', val); else next.delete('view');
            setSearchParams(next, { replace: true });
          };
          const Tab = ({ id, label, onClick, children }) => {
            const on = activeTab === id;
            const col = on ? '#1F6FEB' : '#8A9099';
            return (
              <button type="button" onClick={onClick} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 3, background: 'none', border: 0,
                padding: '7px 0', cursor: 'pointer', color: col,
              }}>
                <span style={{ position: 'relative', lineHeight: 0 }}>{children(col)}</span>
                <span style={{ fontSize: 11, fontWeight: on ? 700 : 600, letterSpacing: '-0.01em' }}>{label}</span>
              </button>
            );
          };
          return (
            <nav style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60,
              display: 'flex', background: '#fff', borderTop: '1px solid #ECEDEF',
              boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              fontFamily: 'Manrope, system-ui, sans-serif',
            }}>
              <Tab id="cerca" label="Cerca" onClick={() => navigate('/app/home')}>
                {(c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke={c} strokeWidth="1.9"/><path d="M21 21l-4.3-4.3" stroke={c} strokeWidth="1.9" strokeLinecap="round"/></svg>)}
              </Tab>
              <Tab id="mappa" label="Mappa" onClick={() => openMapAroundMe(navigate)}>
                {(c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/><circle cx="12" cy="10" r="2.4" stroke={c} strokeWidth="1.8"/></svg>)}
              </Tab>
              <Tab id="preferiti" label="Preferiti" onClick={() => { setNotifyModalOpen(false); setRadarSheetOpen(false); setView('preferiti'); }}>
                {(c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-4.35-9.5-8.5C1 9.5 2.5 5.5 6.5 5.5c2 0 3.5 1.5 5.5 3.5 2-2 3.5-3.5 5.5-3.5 4 0 5.5 4 4 7C19 16.65 12 21 12 21z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/></svg>)}
              </Tab>
              <Tab id="analizzati" label="Analizzati" onClick={() => navigate('/app/analizzati')}>
                {(c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><path d="M4 5h16M4 12h16M4 19h10" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><circle cx="19.5" cy="18.5" r="3" stroke={c} strokeWidth="1.8"/><path d="M21.7 20.7L23 22" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>)}
              </Tab>
              <Tab id="profilo" label="Profilo" onClick={() => navigate('/app/profilo')}>
                {(c) => (<svg width="23" height="23" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={c} strokeWidth="1.8"/><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>)}
              </Tab>
            </nav>
          );
        })()}

        {/* Tab Preferiti senza preferiti salvati → modale (niente mappa vuota) */}
        {isProspect && emptyFavOpen && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 5400, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Manrope, system-ui, sans-serif',
          }}>
            <div style={{ background: '#fff', borderRadius: 20, padding: '26px 22px', width: '100%', maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FDECEA', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.9" strokeLinejoin="round"><path d="M12 21s-7-4.35-9.5-8.5C1 9.5 2.5 5.5 6.5 5.5c2 0 3.5 1.5 5.5 3.5 2-2 3.5-3.5 5.5-3.5 4 0 5.5 4 4 7C19 16.65 12 21 12 21z" /></svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1F2937', marginBottom: 6 }}>Nessun preferito ancora</div>
              <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.45, marginBottom: 20 }}>Salva un immobile col cuore ♡ per ritrovarlo qui.</div>
              <button onClick={() => { const next = new URLSearchParams(searchParams); next.delete('view'); setSearchParams(next, { replace: true }); setEmptyFavOpen(false); }} style={{
                width: '100%', border: 0, background: '#1F6FEB', color: '#fff', borderRadius: 12, padding: '13px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer',
              }}>Esplora immobili</button>
            </div>
          </div>
        )}

        {/* Ricerca salvata modificata → tasto "Salva le modifiche" sopra la tab bar */}
        {isProspect && editSid && saveDirty && (
          <div style={{
            position: 'fixed', left: 12, right: 12, zIndex: 66,
            bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))', maxWidth: 480, margin: '0 auto',
          }}>
            <button onClick={saveSearchChanges} disabled={savingSearch} style={{
              width: '100%', border: 0, background: '#1F6FEB', color: '#fff', borderRadius: 14,
              padding: '15px', fontWeight: 900, fontSize: 15.5, cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(31,111,235,0.45)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
              opacity: savingSearch ? 0.7 : 1, fontFamily: 'Manrope, system-ui, sans-serif',
            }}>
              {savingSearch ? 'Salvataggio…' : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
                  Salva le modifiche
                </>
              )}
            </button>
          </div>
        )}

        {/* Banner "scansione PVP in corso" per una zona nuova ancora senza immobili */}
        {isProspect && scanningZone && (
          <div style={{
            position: 'fixed', left: 12, right: 12, zIndex: 65,
            bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))',
            maxWidth: 480, margin: '0 auto',
            background: '#1F2937', color: '#fff', borderRadius: 14, padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            fontFamily: 'Manrope, system-ui, sans-serif',
          }}>
            <span style={{
              width: 18, height: 18, flex: '0 0 auto', borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
              display: 'inline-block', animation: 'ssSpin2 .8s linear infinite',
            }} />
            <style>{'@keyframes ssSpin2{to{transform:rotate(360deg)}}'}</style>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
              Stiamo cercando gli immobili in questa zona. Ci vuole qualche minuto — ti avvisiamo su WhatsApp appena sono pronti.
            </span>
          </div>
        )}

        {/* Caricamento POI del viewport: pill in alto-centro finché la chiamata
            non è finita. Diverso dallo scan PVP (che è lungo). Marina 2026-07-14. */}
        {isProspect && loading && !scanningZone && (
          <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 120px)', left: 0, right: 0, zIndex: 55, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 9,
              background: '#fff', color: '#1F2937', borderRadius: 999, padding: '8px 15px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.16)', fontFamily: 'Manrope, system-ui, sans-serif',
              fontSize: 13, fontWeight: 700, animation: 'mapLoadIn .2s ease-out both',
            }}>
              <span style={{ width: 15, height: 15, flex: '0 0 auto', borderRadius: '50%', border: '2.5px solid #D9DEE3', borderTopColor: '#1F6FEB', display: 'inline-block', animation: 'mapLoadSpin .8s linear infinite' }} />
              <style>{'@keyframes mapLoadSpin{to{transform:rotate(360deg)}}@keyframes mapLoadIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}'}</style>
              Carico gli immobili in quest'area…
            </div>
          </div>
        )}

        {/* Mini-tutorial, step 0: profilo investitore (4 archetipi + avanzato).
            La scelta va in localStorage (wiring backend con il match per-profilo). */}
        {isProspect && tutorial && tutStep === 'profilo' && (
          <ProfiloInvestitoreStep
            onPick={(profilo) => {
              // profilo = {archetipo:'flipper'|...} oppure {archetipo:'personalizzato', dettagli:{...7 grandezze}}
              try { localStorage.setItem('rivelasta_profilo_investitore', JSON.stringify({ ...profilo, ts: Date.now() })); } catch { /* noop */ }
              setTutStep('poi');
            }}
            onSkip={() => setTutStep('poi')}
            onExit={exitTutorial}
          />
        )}

        {/* Mini-tutorial: spotlight + fumetto (poi / analyze / analyzing / discover).
            Lo step 'analyzing' sparisce da solo appena l'analisi finisce (coriandoli). */}
        {isProspect && tutorial
          && (tutStep === 'poi' || tutStep === 'analyze' || tutStep === 'discover'
              || (tutStep === 'analyzing' && tutImm && !prospectAnalyzedIds.has(tutImm.id))) && (
          <TutorialLayer
            rect={tutRect}
            onSkip={exitTutorial}
            info={{
              poi: { text: 'Scegli l’immobile da analizzare toccando il segnaposto.' },
              analyze: { text: 'Tocca il bottone per analizzare quest’asta con l’intelligenza artificiale e scoprire se è un affare.' },
              analyzing: { text: 'Ora attendi l’esito dell’analisi fatta con l’intelligenza artificiale…', hideTap: true },
              discover: { text: 'Analisi pronta! Premi qui per aprire il verdetto.' },
            }[tutStep]}
          />
        )}

        {/* Mini-tutorial: spiegazione del verdetto. Il report resta VISIBILE ma sotto
            un overlay non cliccabile finché non premi "Leggi il report": a quel punto
            il tutorial finisce e puoi leggere/navigare il report liberamente. */}
        {isProspect && tutorial && tutStep === 'verdict' && (
          <>
            {/* overlay che blocca l'interazione col report (lo lascia intravedere) */}
            <div onClick={(e) => e.stopPropagation()}
                 style={{ position: 'fixed', inset: 0, zIndex: 5500, background: 'rgba(15,23,42,0.42)', pointerEvents: 'auto' }} />
            <div style={{ position: 'fixed', left: 14, right: 14, bottom: 'calc(22px + env(safe-area-inset-bottom,0px))', zIndex: 5600, display: 'flex', justifyContent: 'center', pointerEvents: 'none', fontFamily: 'Manrope, system-ui, sans-serif' }}>
              <div style={{ maxWidth: 360, width: '100%', background: '#fff', borderRadius: 16, padding: '15px 16px', boxShadow: '0 16px 44px rgba(0,0,0,0.4)', pointerEvents: 'auto' }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.42, color: '#1F2937' }}>In questa scheda trovi i <b>veri valori</b>, i <b>rilanci consigliati</b> e i <b>rischi</b> dell’asta.</div>
                <button onClick={() => {
                  // Fine tutorial: sblocca il report e lascialo leggere. Usciamo dalla
                  // modalità tutorial (niente più overlay) ma teniamo aperto il report.
                  // Flag: chiudendo QUESTO report (fine tutorial) si torna alla Home.
                  tutorialActiveRef.current = false;
                  tutorialEndReportRef.current = true;
                  setTutStep(null); setTutImm(null);
                  const next = new URLSearchParams(searchParams);
                  next.delete('tutorial');
                  setSearchParams(next, { replace: true });
                }} style={{ marginTop: 13, width: '100%', border: 0, background: '#16915E', color: '#fff', borderRadius: 12, padding: '12px', fontWeight: 800, fontSize: 14.5, cursor: 'pointer' }}>Leggi il report</button>
              </div>
            </div>
          </>
        )}

        {/* Fase "arm": mappa ancora libera — posiziona/zooma, poi premi Disegna */}
        {isProspect && drawArm && (
          <>
            <div style={{
              position: 'fixed', left: 12, right: 12, top: 12, zIndex: 200,
              background: '#fff', color: '#1F2937', borderRadius: 14,
              padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: '0 8px 30px rgba(0,0,0,0.18)', fontFamily: 'Manrope, system-ui, sans-serif',
            }}>
              <span style={{ fontSize: 20 }}>🗺️</span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
                Sposta e ingrandisci la mappa sulla zona, poi premi <b>Disegna</b>.
              </span>
              <button onClick={() => setDrawArm(false)} style={{
                border: 0, background: '#F1F3F5', color: '#6B7280',
                borderRadius: 9, padding: '7px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>Annulla</button>
            </div>
            <button onClick={() => { setDrawArm(false); setDrawMode(true); }} style={{
              position: 'fixed', left: '50%', transform: 'translateX(-50%)',
              bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))', zIndex: 200,
              display: 'inline-flex', alignItems: 'center', gap: 9,
              background: '#1F6FEB', color: '#fff', border: 0, borderRadius: 999,
              padding: '14px 26px', fontWeight: 800, fontSize: 15, cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(31,111,235,0.45)', fontFamily: 'Manrope, system-ui, sans-serif',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><circle cx="11" cy="11" r="2" />
              </svg>
              Disegna l'area
            </button>
          </>
        )}

        {/* Fase disegno attivo: mappa bloccata, si traccia col dito */}
        {isProspect && drawMode && (
          <div style={{
            position: 'fixed', left: 12, right: 12, top: 12, zIndex: 200,
            background: '#1F2937', color: '#fff', borderRadius: 14,
            padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.30)', fontFamily: 'Manrope, system-ui, sans-serif',
          }}>
            <span style={{ fontSize: 20 }}>✏️</span>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
              Tieni premuto e trascina per disegnare il perimetro.
            </span>
            <button onClick={() => { setDrawMode(false); setDrawArm(true); }} style={{
              border: 0, background: 'rgba(255,255,255,0.16)', color: '#fff',
              borderRadius: 9, padding: '7px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>Annulla</button>
          </div>
        )}

        {/* Sheet "Scegli la zona": autocomplete + disegna area */}
        {isProspect && zoneSheetOpen && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)',
            zIndex: 4000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }} onClick={() => setZoneSheetOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480,
              padding: '10px 18px calc(20px + env(safe-area-inset-bottom, 0px))',
              boxShadow: '0 -10px 40px rgba(0,0,0,0.30)', maxHeight: '85vh', overflow: 'auto',
              fontFamily: 'Manrope, system-ui, sans-serif',
              animation: 'prSheetUpZ .22s cubic-bezier(.22,.61,.36,1)',
            }}>
              <style>{'@keyframes prSheetUpZ{from{transform:translateY(100%)}to{transform:translateY(0)}}'}</style>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0 12px' }}>
                <span style={{ width: 40, height: 5, borderRadius: 3, background: '#DDE1E6' }} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1F2937', marginBottom: 4 }}>Scegli la zona</div>
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
                Cerca un indirizzo, una città o una regione — oppure disegna tu l'area sulla mappa.
              </div>

              <AddressAutocomplete
                value={zoneSearch}
                onChange={setZoneSearch}
                onSelect={applyZoneFromPlace}
                placeholder="es. Milano, oppure Via Roma 10, oppure Lazio"
                inputClassName="fp__input"
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', color: '#9AA0A6', fontSize: 12 }}>
                <span style={{ flex: 1, height: 1, background: '#ECEDEF' }} /> oppure <span style={{ flex: 1, height: 1, background: '#ECEDEF' }} />
              </div>

              <button onClick={() => { setZoneSheetOpen(false); setDrawArm(true); }} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                border: '1.5px solid #E4E6EA', background: '#fff', textAlign: 'left',
              }}>
                <span style={{
                  width: 40, height: 40, borderRadius: 10, background: '#EAF2FF',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" />
                  </svg>
                </span>
                <span style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1F2937' }}>Disegna un'area sulla mappa</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280' }}>Traccia col dito la zona che ti interessa</div>
                </span>
              </button>

              {(drawnArea || searchParams.get('center_label')) && (
                <button onClick={clearZone} style={{
                  width: '100%', marginTop: 12, padding: '13px 16px', borderRadius: 14,
                  border: 0, background: '#FDECEA', color: '#C2453B', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                }}>Rimuovi zona attiva</button>
              )}
            </div>
          </div>
        )}

        {/* Sheet di setup del Radar */}
        {isProspect && radarSheetOpen && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }} onClick={() => setRadarSheetOpen(false)}>
            <div style={{
              background: 'white', borderRadius: '16px 16px 0 0', width: '100%',
              maxWidth: 480, padding: 20, boxShadow: '0 -10px 40px rgba(0,0,0,0.30)',
              maxHeight: '85vh', overflow: 'auto',
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📡 Il tuo radar</h3>
                <button onClick={() => setRadarSheetOpen(false)} style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer' }}>✕</button>
              </div>
              <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
                Dicci cosa stai cercando. Quando troviamo immobili in asta che corrispondono,
                ti scriviamo <strong>direttamente su WhatsApp</strong>{radarPhone ? ` al numero ${radarPhone}` : ''}.
              </p>

              <label style={radarLbl}>Budget massimo (€)</label>
              <input type="number" inputMode="numeric" min="0" step="1000"
                     placeholder="es. 150000"
                     value={radarForm.prezzo_max}
                     onChange={(e) => setRadarForm((f) => ({ ...f, prezzo_max: e.target.value }))}
                     style={radarInput} />

              <label style={radarLbl}>Tipo di immobile</label>
              <select value={radarForm.tipologia}
                      onChange={(e) => setRadarForm((f) => ({ ...f, tipologia: e.target.value }))}
                      style={radarInput}>
                <option value="">Qualsiasi</option>
                <option value="appartamento">Appartamento</option>
                <option value="civ">Civile</option>
                <option value="eco">Economico</option>
                <option value="villin">Villini</option>
                <option value="ville">Ville</option>
              </select>

              <label style={radarLbl}>Comune (opzionale)</label>
              <input type="text" placeholder="es. Latina"
                     value={radarForm.comune}
                     onChange={(e) => setRadarForm((f) => ({ ...f, comune: e.target.value }))}
                     style={radarInput} />

              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                {radarOn && (
                  <button type="button"
                          onClick={async () => {
                            await saveProspectRadar({});
                            setRadarOn(false);
                            setRadarSheetOpen(false);
                            setRadarToast({ kind: 'off', text: 'Radar disattivato.' });
                            setTimeout(() => setRadarToast(null), 4000);
                          }}
                          style={{ flex: 1, padding: '11px', borderRadius: 8, border: '1px solid var(--border)',
                                   background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                                   color: 'oklch(0.50 0.15 25)' }}>
                    Disattiva
                  </button>
                )}
                <button type="button"
                        onClick={async () => {
                          const criteria = {};
                          if (radarForm.prezzo_max) criteria.prezzo_max = Number(radarForm.prezzo_max);
                          if (radarForm.tipologia) criteria.tipologia_categoria = radarForm.tipologia;
                          if (radarForm.comune) criteria.comune = radarForm.comune;
                          await saveProspectRadar(criteria);
                          setRadarOn(true);
                          setRadarSheetOpen(false);
                          setRadarToast({
                            kind: 'on',
                            text: `Radar attivato! Riceverai su WhatsApp${radarPhone ? ' al ' + radarPhone : ''} tutti gli immobili in asta che corrispondono ai tuoi criteri.`,
                          });
                          setTimeout(() => setRadarToast(null), 7000);
                        }}
                        style={{ flex: 2, padding: '11px', borderRadius: 8, border: 0,
                                 background: 'linear-gradient(135deg, oklch(0.55 0.18 145), oklch(0.62 0.20 160))',
                                 color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700, letterSpacing: '0.03em' }}>
                  {radarOn ? 'Aggiorna radar' : 'Attiva radar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale report HTML: iframe full-screen con ✕ in alto a destra */}
        {reportUrl && (
          <div className="pr-report-modal" role="dialog" aria-label="Report analisi AI">
            <div className="pr-report-modal__backdrop" onClick={closeReport} />
            {/* ✕ figlia del contenitore (fixed), NON dello sheet: così sta nella
                fascia sopra il modale e non si sovrappone al verdetto. */}
            <button type="button" className="pr-report-modal__close"
                    onClick={closeReport}
                    aria-label="Chiudi">×</button>
            <div className="pr-report-modal__sheet">
              <iframe className="pr-report-modal__iframe"
                      src={reportUrl} title="Analisi immobile" />
            </div>
          </div>
        )}

        {/* Toast di conferma radar */}
        {isProspect && radarToast && (
          <div style={{
            position: 'fixed', left: 16, right: 16, bottom: 88, zIndex: 90,
            background: radarToast.kind === 'on' ? 'oklch(0.95 0.10 145)' : 'oklch(0.96 0.04 25)',
            border: radarToast.kind === 'on' ? '1px solid oklch(0.55 0.18 145)' : '1px solid oklch(0.75 0.12 25)',
            color: radarToast.kind === 'on' ? 'oklch(0.30 0.13 145)' : 'oklch(0.35 0.15 25)',
            padding: '12px 16px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.4,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            maxWidth: 480, margin: '0 auto',
          }}>
            {radarToast.kind === 'on' ? '✅ ' : ''}{radarToast.text}
          </div>
        )}

        {isProspect && carousel && carousel.length > 0 && (() => {
          // Replica carosello design BTC: track con translateX, no native scroll, peek card successiva.
          const carN = carousel.length;
          const hasStack = carN > 1;
          // viewport width capped a 390px (mobile), -28px di padding orizzontale del wrap = larghezza utile;
          // card stacked = util - 56 (per lasciare ~56px di peek), card single = util.
          const vw = typeof window !== 'undefined' ? Math.min(window.innerWidth, 720) : 390;
          // Card più larga in modalità stack: lascia solo un piccolo peek (28px) della card successiva.
          // Prima era 84 → card 322px su 390vw lasciando 68px = troppo peek; ora 56 → card più larga.
          const cardW = hasStack ? Math.min(vw - 56, 344) : Math.min(vw - 28, 362);
          const gap = 12;
          const step = cardW + gap;
          const idx = Math.min(carIndex, carN - 1);
          const navDisabled = !!(prospectAn && prospectAn.phase && prospectAn.phase !== 'reveal');
          const canDrag = hasStack && !navDisabled;
          const dragDx = carDrag.active ? carDrag.dx : 0;
          const trackTransform = `translateX(${-(idx * step) + dragDx}px)`;
          const onDragStart = (e) => {
            if (!canDrag) return;
            carDragRef.current = { startX: e.clientX, pointerId: e.pointerId, captured: false };
            setCarDrag({ active: true, dx: 0 });
          };
          const onDragMove = (e) => {
            if (!carDrag.active) return;
            let dx = e.clientX - carDragRef.current.startX;
            if (!carDragRef.current.captured && Math.abs(dx) > 6) {
              carDragRef.current.captured = true;
              try { e.currentTarget.setPointerCapture(carDragRef.current.pointerId); } catch (_) {}
            }
            // Resistenza ai bordi: rallenta drag quando si è alla prima/ultima card
            if ((idx === 0 && dx > 0) || (idx === carN - 1 && dx < 0)) dx *= 0.32;
            setCarDrag({ active: true, dx });
          };
          const onDragEnd = () => {
            if (!carDrag.active) return;
            const dx = carDrag.dx;
            const TH = 48;
            let ni = idx;
            if (dx <= -TH && idx < carN - 1) ni = idx + 1;
            else if (dx >= TH && idx > 0) ni = idx - 1;
            setCarDrag({ active: false, dx: 0 });
            setCarIndex(ni);
          };
          return (
          <div className="pr-carousel-wrap">
            <div className="pr-carousel-viewport"
                 onPointerDown={onDragStart}
                 onPointerMove={onDragMove}
                 onPointerUp={onDragEnd}
                 onPointerCancel={onDragEnd}>
              <div className="pr-carousel-track"
                   style={{
                     gap: `${gap}px`,
                     transform: trackTransform,
                     transition: carDrag.active ? 'none' : 'transform .36s cubic-bezier(.22,.7,.2,1)',
                     touchAction: 'pan-y',
                   }}>
              {carousel.map((r, mi) => {
                const anActive = prospectAn && prospectAn.rowId === r.id;
                return (
                  <ProspectCard
                    key={r.id}
                    row={r}
                    width={cardW + 'px'}
                    selfReveal
                    analysis={anActive ? prospectAn : null}
                    analyzed={prospectAnalyzedIds.has(r.id)}
                    onClose={() => setCarousel(null)}
                    onPhoto={setStreetViewPano}
                    onToggleFav={toggleProspectFav}
                    onAnalyze={startProspectAnalysis}
                    onReport={(url) => {
                      setHint(null);
                      // Nel tutorial il report va aperto in bypass del cap/paywall.
                      // NB: da 16/09/2026 `url` è la rotta autenticata
                      // /api/immobili/{id}/report-html, non più il file grezzo; il
                      // parametro del tutorial resta innocuo e si tiene per non
                      // toccare il flusso del mini-tutorial.
                      const u = (tutorialActiveRef.current && url && !url.includes('tutorial='))
                        ? url + (url.includes('?') ? '&' : '?') + 'tutorial=1'
                        : url;
                      setReportUrl(u);
                    }}
                    onShare={(row, btn) => condividiReportProspect(row, btn)}
                    onAnalyzePvp={async (row) => {
                      try {
                        const res = await startAstemaster(row.id);
                        const nextStatus = res.status || 'queued';
                        setCarousel((c) => c && c.map((item) => item.id === row.id ? { ...item, job_status: nextStatus } : item));
                        setRows((rs) => rs.map((item) => item.id === row.id ? { ...item, job_status: nextStatus } : item));
                      } catch (e) {
                        window.alert(e?.response?.data?.error || 'Impossibile avviare l\'analisi');
                      }
                    }}
                  />
                );
              })}
              </div>
            </div>
            {hasStack && (
              <div className="pr-carousel__dots-wrap">
                <div className="pr-carousel__dots">
                  {carousel.map((_, i) => (
                    <button key={i} type="button"
                            className="pr-carousel__dot"
                            data-active={i === idx ? 'true' : 'false'}
                            disabled={navDisabled}
                            onClick={() => !navDisabled && setCarIndex(i)} />
                  ))}
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* Verdetto quick-selector — pill row centrata in alto (nascosto per prospect) */}
        {!isProspect && <div
          style={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 5,
            display: 'flex',
            gap: 4,
            padding: 4,
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            boxShadow: '0 2px 6px oklch(0 0 0 / 0.15)',
          }}
        >
          {[
            { id: '',            color: 'transparent', border: 'var(--fg-muted)', title: 'Tutti i verdetti' },
            { id: 'eccezionale', color: '#16a34a', title: '⭐ Eccezionale' },
            { id: 'consigliata', color: '#65a30d', title: '✓ Consigliata' },
            { id: 'con_riserva', color: '#d97706', title: '⚠ Con riserva' },
            { id: 'da_evitare',  color: '#dc2626', title: '✕ Da evitare' },
            { id: 'in_analisi',  color: '#2563eb', title: '🔵 In analisi (job attivo)' },
            { id: 'da_analizzare', color: '#737373', title: 'Da analizzare' },
          ].map((v) => {
            const isTutti = v.id === '';
            const active = isTutti ? verdettoSet.size === 0 : verdettoSet.has(v.id);
            return (
              <button
                key={v.id || 'all'}
                type="button"
                onClick={() => toggleVerdetto(v.id)}
                title={v.title}
                aria-label={v.title}
                aria-pressed={active}
                style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: v.color,
                  border: isTutti
                    ? `2px solid ${v.border}`
                    : (active ? '2px solid var(--fg)' : '2px solid white'),
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 10, fontWeight: 700,
                  outline: active ? '2px solid white' : 'none',
                  outlineOffset: active ? -4 : 0,
                  transition: 'transform 0.08s',
                  transform: active ? 'scale(1.1)' : 'scale(1)',
                  padding: 0,
                }}
              >
                {isTutti ? '·' : (active ? '✓' : '')}
              </button>
            );
          })}
        </div>}

        {/* Filtri — in alto a sinistra (nascosto per prospect: barra dedicata sopra) */}
        {!isProspect && <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 5,
          }}
        >
          <button
            type="button"
            onClick={() => setFilterPanelOpen(true)}
            title="Filtri"
            aria-label="Filtri"
            style={{
              width: 38, height: 38,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.96)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              boxShadow: '0 2px 6px oklch(0 0 0 / 0.15)',
              color: 'var(--fg)',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <IFilter size={18} />
            {activeFilterCount > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                background: 'var(--fg)', color: 'var(--bg-elev)',
                fontSize: 10, fontWeight: 700,
                minWidth: 18, height: 18, padding: '0 4px',
                borderRadius: 9,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--bg-elev)',
              }}>{activeFilterCount}</span>
            )}
          </button>
        </div>}

        {/* Fullscreen — in alto a destra (nascosto per prospect: già a tutto schermo) */}
        {!isProspect && <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 5,
          }}
        >
          <button
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            title={fullscreen ? 'Esci da schermo intero (Esc)' : 'Schermo intero'}
            aria-label={fullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
            style={{
              width: 38, height: 38,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.96)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              boxShadow: '0 2px 6px oklch(0 0 0 / 0.15)',
              color: 'var(--fg)',
              cursor: 'pointer',
            }}
          >
            {fullscreen ? <ICollapse size={18} /> : <IExpand size={18} />}
          </button>
        </div>}

        {error && (
          <div style={{
            position: 'absolute', top: 16, left: 16, padding: '10px 14px',
            background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)',
            color: 'oklch(0.35 0.15 25)', borderRadius: 8, fontSize: 13, zIndex: 1000,
          }}>{error}</div>
        )}

        {/* Legenda collassabile (nascosta in modalità prospect) */}
        {!isProspect && <div style={{ position: 'absolute', bottom: 28, left: 16, zIndex: 5 }}>
          {!legendOpen && (
            <button
              type="button"
              onClick={() => setLegendOpen(true)}
              title="Mostra legenda"
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.96)',
                border: '1px solid var(--border)',
                boxShadow: '0 2px 6px oklch(0 0 0 / 0.15)',
                color: 'var(--fg)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, fontStyle: 'italic',
              }}
            >i</button>
          )}
          {legendOpen && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.97)',
              border: '1px solid var(--border)',
              borderRadius: 8, fontSize: 11, lineHeight: 1.7,
              boxShadow: '0 2px 6px oklch(0 0 0 / 0.15)',
              animation: 'rv-fade-in 0.15s ease',
              // Bound al viewport (usa dvh che si adatta alla UI mobile dinamica)
              maxHeight: 'calc(100dvh - 180px)',
              overflowY: 'auto',
              minWidth: 130,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontWeight: 600, marginBottom: 4, gap: 12,
              }}>
                <span>Legenda</span>
                <button type="button" onClick={() => setLegendOpen(false)}
                  aria-label="Chiudi legenda"
                  style={{
                    background: 'transparent', border: 0, cursor: 'pointer',
                    color: 'var(--fg-muted)', padding: 0, lineHeight: 1, fontSize: 14,
                  }}>×</button>
              </div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#16a34a',marginRight:6}}/>Eccezionale</div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#65a30d',marginRight:6}}/>Consigliata</div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#d97706',marginRight:6}}/>Con riserva</div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#dc2626',marginRight:6}}/>Da evitare</div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#2563eb',marginRight:6}}/>In analisi</div>
              <div><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#737373',marginRight:6}}/>Non analizzato</div>
              <div style={{marginTop:4}}><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',border:'2px solid #f97316',marginRight:6}}/>Preferito</div>
            </div>
          )}
        </div>}
      </div>

      <FilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        filterOptions={filterOptions}
      />
      {isProspect && confetti && (
        <div className="pr-confetti-fullscreen" aria-hidden="true">
          {confettiPieces}
        </div>
      )}
      {emptyFilterModalOpen && (
        <div className="pr-limit-modal" role="dialog" aria-label="Nessun immobile scoperto">
          <div className="pr-limit-modal__backdrop" onClick={() => setEmptyFilterModalOpen(false)} />
          <div className="pr-limit-modal__sheet">
            <button className="pr-limit-modal__close" onClick={() => setEmptyFilterModalOpen(false)} aria-label="Chiudi">×</button>
            <div className="pr-limit-modal__icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M3.5 20.5L13 11" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                <path d="M16 3.8L17.4 6.6L20.2 8L17.4 9.4L16 12.2L14.6 9.4L11.8 8L14.6 6.6Z" fill="#fff"/>
              </svg>
            </div>
            <div className="pr-limit-modal__title">Ancora nessun immobile scoperto</div>
            <div className="pr-limit-modal__body">
              Non hai ancora analizzato immobili di questa categoria. Tocca un pallino sulla mappa e avvia un'analisi per scoprire il verdetto AI.
            </div>
            <button className="pr-limit-modal__cta" type="button"
                    onClick={() => setEmptyFilterModalOpen(false)}>
              <span>Ho capito</span>
            </button>
          </div>
        </div>
      )}
      {pendingAnalysisModalOpen && (
        <div className="pr-limit-modal" role="dialog" aria-label="Analisi in elaborazione">
          <div className="pr-limit-modal__backdrop" onClick={() => setPendingAnalysisModalOpen(false)} />
          <div className="pr-limit-modal__sheet">
            <button className="pr-limit-modal__close" onClick={() => setPendingAnalysisModalOpen(false)} aria-label="Chiudi">×</button>
            <div className="pr-limit-modal__icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
              </svg>
            </div>
            <div className="pr-limit-modal__title">Analisi avviata</div>
            <div className="pr-limit-modal__body">
              Abbiamo avviato l'analisi di questo immobile. Richiede qualche minuto:
              <b> riceverai il verdetto direttamente sul tuo WhatsApp</b>, con il link
              per vederlo sulla mappa.
            </div>
            <button className="pr-limit-modal__cta" type="button"
                    onClick={() => setPendingAnalysisModalOpen(false)}>
              <span>Ho capito</span>
            </button>
          </div>
        </div>
      )}
      {notifyModalOpen && (
        <div className="pr-limit-modal" role="dialog" aria-label="Notifiche WhatsApp">
          <div className="pr-limit-modal__backdrop" onClick={() => setNotifyModalOpen(false)} />
          <div className="pr-limit-modal__sheet">
            <button className="pr-limit-modal__close" onClick={() => setNotifyModalOpen(false)} aria-label="Chiudi">×</button>
            <div className="pr-limit-modal__icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
              </svg>
            </div>
            <div className="pr-limit-modal__title">
              {waNotifEnabled ? 'Notifiche WhatsApp attive' : 'Notifiche WhatsApp disattivate'}
            </div>
            <div className="pr-limit-modal__body">
              {waNotifEnabled
                ? <>Ti avvisiamo su <b>WhatsApp</b> ogni volta che il tuo radar scopre un nuovo immobile che corrisponde ai tuoi criteri.</>
                : <>Le notifiche WhatsApp sono disattivate. Non riceverai più gli avvisi sui nuovi immobili scoperti dal tuo radar.</>
              }
            </div>
            <a className="pr-limit-modal__cta"
               href={`https://wa.me/393000000000?text=${encodeURIComponent('Ciao, vorrei parlare con lo studio legale per informazioni sul mio radar Rivelasta.')}`}
               target="_blank" rel="noopener noreferrer"
               onClick={() => setNotifyModalOpen(false)}>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="#fff">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
              </svg>
              <span>Contatta lo studio legale</span>
            </a>
            <button className="pr-limit-modal__later" onClick={() => setNotifyModalOpen(false)}>Chiudi</button>
          </div>
        </div>
      )}
      {limitModalOpen && (
        <div className="pr-limit-modal" role="dialog" aria-label="Analisi esaurite">
          <div className="pr-limit-modal__backdrop" onClick={() => setLimitModalOpen(false)} />
          <div className="pr-limit-modal__sheet">
            <button className="pr-limit-modal__close" onClick={() => setLimitModalOpen(false)} aria-label="Chiudi">×</button>
            <div className="pr-limit-modal__icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M3.5 20.5L13 11" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                <path d="M16 3.8L17.4 6.6L20.2 8L17.4 9.4L16 12.2L14.6 9.4L11.8 8L14.6 6.6Z" fill="#4FD89E"/>
              </svg>
            </div>
            <div className="pr-limit-modal__title">Hai esaurito le analisi gratuite</div>
            <div className="pr-limit-modal__body">
              Hai usato tutte e <b>{PROSPECT_ANALYSIS_LIMIT}</b> le analisi AI del tuo accesso di prova. Sblocca le analisi illimitate e il monitoraggio 24/7 su 89 tribunali.
            </div>
            <div className="pr-limit-modal__stat">
              <div className="pr-limit-modal__dots">
                {Array.from({ length: PROSPECT_ANALYSIS_LIMIT }).map((_, i) => (
                  <span key={i} className="pr-limit-modal__dot" />
                ))}
              </div>
              <span>{prospectViewedCount}/{PROSPECT_ANALYSIS_LIMIT} analisi utilizzate</span>
            </div>
            <a className="pr-limit-modal__cta"
               href={`https://wa.me/393000000000?text=${encodeURIComponent('Ciao! Ho esaurito le analisi gratuite su Rivelasta e vorrei richiedere un accesso esteso.')}`}
               target="_blank" rel="noopener noreferrer">
              <svg width="21" height="21" viewBox="0 0 24 24" fill="#fff">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.06c-.25.7-1.44 1.33-1.99 1.41-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.6.84 2.06.91 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.65-.07.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.71.81 2.01.96.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/>
              </svg>
              <span>Richiedi accesso su WhatsApp</span>
            </a>
            <button className="pr-limit-modal__later" onClick={() => setLimitModalOpen(false)}>Più tardi</button>
          </div>
        </div>
      )}
      {streetViewPano && (
        <div className="pr-streetview-modal" role="dialog" aria-label="Street View">
          <button className="pr-streetview-modal__close"
                  onClick={() => setStreetViewPano(null)}
                  aria-label="Chiudi">×</button>
          <iframe
            title="Street View"
            src={`https://www.google.com/maps/embed/v1/streetview?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&location=${streetViewPano.lat},${streetViewPano.lng}&fov=80`}
            allow="fullscreen"
          />
        </div>
      )}
      {omiRow && (
        <OmiReportModal row={omiRow} onClose={() => setOmiRow(null)} />
      )}
    </main>
  );
}

// Tooltip ancorato SOTTO il bottone Ottimi — allineato al lato sinistro del bottone
// e largo fino al viewport (Ottimi è il primo bottone della riga, vicino al bordo sx).
const anchoredHintBelow = {
  position: 'absolute',
  top: 'calc(100% + 12px)',
  left: 0,
  background: 'oklch(0.25 0.04 250)',
  color: '#fff',
  padding: '10px 32px 10px 14px',
  borderRadius: 10,
  width: 260,
  maxWidth: 'calc(100vw - 28px)',
  boxShadow: '0 8px 24px oklch(0 0 0 / 0.25)',
  zIndex: 61, // sopra l'overlay modale (50)
  pointerEvents: 'auto',
};
// CSS keyframes (iniettati una volta nel DOM)
if (typeof document !== 'undefined' && !document.getElementById('pulse-spot-css')) {
  const s = document.createElement('style');
  s.id = 'pulse-spot-css';
  s.textContent = `
    @keyframes pulse-spot{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.18);opacity:0.85}}
    @keyframes radar-pulse{
      0%{box-shadow:0 0 0 0 rgba(255,255,255,0.7),0 0 0 4px rgba(255,255,255,0.35)}
      70%{box-shadow:0 0 0 14px rgba(255,255,255,0),0 0 0 4px rgba(255,255,255,0.35)}
      100%{box-shadow:0 0 0 0 rgba(255,255,255,0),0 0 0 4px rgba(255,255,255,0.35)}
    }
  `;
  document.head.appendChild(s);
}

// Stili input sheet radar
const radarLbl = { display: 'block', fontSize: 12, fontWeight: 600, color: 'oklch(0.35 0 0)', marginTop: 12, marginBottom: 4 };
const radarInput = { width: '100%', padding: '10px 12px', border: '1px solid oklch(0.85 0 0)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box' };

// Overlay modale che oscura tutto tranne il bottone Ottimi + tooltip
const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 50,
};
// Variante: visivamente identica ma click pass-through (così i markers restano cliccabili)
const modalOverlayPassthrough = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 50,
  pointerEvents: 'none',
};
// Tooltip "tocca un pallino" — ancorato in alto al centro della mappa
const anchoredHintOnMap = {
  position: 'absolute',
  top: 12,
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'oklch(0.25 0.04 250)',
  color: '#fff',
  padding: '10px 32px 10px 14px',
  borderRadius: 10,
  width: 260,
  maxWidth: 'calc(100vw - 28px)',
  boxShadow: '0 8px 24px oklch(0 0 0 / 0.25)',
  zIndex: 61, // sopra l'overlay (50)
  pointerEvents: 'auto',
};
const hintCloseBtn = {
  position: 'absolute',
  top: 4, right: 4,
  background: 'transparent', border: 0, color: '#fff', cursor: 'pointer',
  fontSize: 14, padding: 4,
};
const arrowDownAbsBottom = {
  position: 'absolute',
  bottom: -8, left: '50%', transform: 'translateX(-50%)',
  width: 0, height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderTop: '8px solid oklch(0.25 0.04 250)',
};
// Freccia in alto, allineata al centro del bottone Ottimi (largo ~70px → ~35px)
const arrowUpAbsTop = {
  position: 'absolute',
  top: -8, left: 28,
  width: 0, height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderBottom: '8px solid oklch(0.25 0.04 250)',
};
